import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import NoticeLibraryCard from '@/components/seller/live/NoticeLibraryCard';
import useNoticeLibrary from '@/hooks/useNoticeLibrary';
// STEP MINI-ORDER-V1 · 판매자 미니주문 운영 패널 (URL/QR/주문관리 shortcut).
// STEP MINI-CONSOLE-STUDIO-UX-1 · 분해된 섹션 임포트 — 메인 승격 + 사이드 compact.
import MiniOrderPanel, {
  SelfLivePanel,
  ExternalMiniSection,
} from '@/components/seller/live/MiniOrderPanel';
// STEP LIVE-CONSOLE-OVERLAY-COLLAPSE-PIN-1 · Console-only 접기/고정 wrapper
// + seller-scoped UI preference (지시 §12 · §6).  Overlay runtime state 와 분리.
import CollapsibleControlSection from '@/components/seller/live/CollapsibleControlSection';
import useOverlayPanelsPreference from '@/hooks/useOverlayPanelsPreference';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  ArrowLeft, Radio, Megaphone, Plus, Trash2, Pencil, Check, Download,
  ChevronLeft, ChevronRight, XCircle, StopCircle, Zap, ArrowUp, ArrowDown, X, Share2,
  Search, Bell, History, ExternalLink, Youtube, Wifi, WifiOff, AlertTriangle, Link2,
  Camera, Upload, Mic, Loader2, Square, Copy,
} from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import ShareModal from '@/components/ShareModal';
import OrderTestConsole from '@/components/OrderTestConsole';
import OverlayTimerControl from '@/components/OverlayTimerControl';
import CompactLiveSummary from '@/components/seller/live/CompactLiveSummary';
// STEP LIVE-CONSOLE-ROLE-SPLIT-UX-1 · 외부 LIVE 모니터 새 창 열기 (지시 §7 §8).
import BroadcastMonitorCard from '@/components/seller/live/BroadcastMonitorCard';
import LiveChannelsCard from '@/components/seller/live/LiveChannelsCard';
import TikTokAccountCard from '@/components/seller/live/TikTokAccountCard';
import InstagramAccountCard from '@/components/seller/live/InstagramAccountCard';
import LiveReportModal from '@/components/seller/live/LiveReportModal';
// STEP LIVE-CONSOLE-FINAL-UI-POLISH-1 · Console 에서 Camera/Mic/LiveKit publish 중복 UI 제거 (지시 §11).
// LiveStreamCard import 삭제 — 역할은 Studio (`SellerLiveConsole.js`) 가 전담.
// STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · Common LIVE Operations hook REUSE.
// announce / preset / position / size_table / inventory / QR / goto 모두
// 동일한 canonical contract (/announce) 을 사용하도록 wiring.
import useLiveOperations from '@/hooks/useLiveOperations';
// STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · Shared Quick Register modal + Notice editor hook.
import QuickAddModal from '@/components/seller/live/QuickAddModal';
import CaptureInbox, { markCaptureRegistered } from '@/components/seller/live/CaptureInbox';
import { UpcomingPicker, loadUpcoming } from '@/components/seller/live/UpcomingPicker';
import { TEST_SID, buildTestShared, presetOps, testOps, toConsoleData, useTestLiveShare } from '@/lib/testLiveShare';
import { TimerPresetList, ExternalNoticeList } from '@/components/seller/live/LivePresetPanel';
import PastSlideStatus from '@/components/seller/live/PastSlideStatus';
import { useShortToast, readHidePref, writeHidePref } from '@/lib/useShortToast';
import { useLivePresets, realPresetCtx, makeRealApply, realTimer } from '@/lib/livePresetsReal';
import LivePreviewModal from '@/components/seller/live/LivePreviewModal';
import { useNoticeEditorAutoHydrate } from '@/hooks/useNoticeEditor';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// ---- Slot number inline editor ------------------------------------------
const SlotEditor = ({ current, onSave, onCancel }) => {
  const [v, setV] = useState(String(current));
  return (
    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input type="number" min={1} value={v} onChange={(e) => setV(e.target.value)}
        className="w-12 px-1 py-0.5 border border-black/10 dark:border-white/10 rounded bg-transparent text-xs text-center"
        data-testid="slot-input" autoFocus />
      <button onClick={() => onSave(Number(v))} className="p-1 rounded bg-[#FF3B30] text-white" data-testid="slot-save"><Check size={12} /></button>
      <button onClick={onCancel} className="p-1 rounded border border-black/10 dark:border-white/10 text-xs">✕</button>
    </div>
  );
};

// ---- Bulk import from a past broadcast (multi-select) --------------------
const ImportModal = ({ open, onClose, currentScheduleId, onImported }) => {
  const [past, setPast] = useState([]);
  const [pick, setPick] = useState('');
  const [srcItems, setSrcItems] = useState([]);
  const [existingIds, setExistingIds] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get('/seller/broadcasts');
        const list = (res.data || []).filter((b) => b.schedule_id !== currentScheduleId);
        list.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
        setPast(list);
        setPick(list[0]?.schedule_id || '');
        if (currentScheduleId) {
          const cur = await api.get(`/seller/broadcasts/${currentScheduleId}/live-products`);
          setExistingIds(new Set((cur.data?.items || []).map((it) => it.product_id)));
        }
      } catch (e) { toast.error('지난 방송 목록을 불러오지 못했습니다.'); }
    })();
  }, [open, currentScheduleId]);

  useEffect(() => {
    if (!pick) { setSrcItems([]); setSelected(new Set()); return; }
    (async () => {
      try {
        const res = await api.get(`/seller/broadcasts/${pick}/live-products`);
        const items = res.data?.items || [];
        setSrcItems(items);
        // Pre-select every non-duplicate item.
        const preselected = new Set(items.filter((it) => !existingIds.has(it.product_id)).map((it) => it.product_id));
        setSelected(preselected);
      } catch { setSrcItems([]); setSelected(new Set()); }
    })();
  }, [pick, existingIds]);

  if (!open) return null;

  const toggle = (pid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(pid)) n.delete(pid); else n.add(pid);
    return n;
  });

  const doImport = async () => {
    if (!pick) return;
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('가져올 상품을 하나 이상 선택해주세요.'); return; }
    setLoading(true);
    try {
      const r = await api.post(`/seller/broadcasts/${currentScheduleId}/import-products`, { from_schedule_id: pick, product_ids: ids });
      toast.success(`${r.data.imported}건 불러옴${r.data.skipped_duplicates ? ` · 중복 ${r.data.skipped_duplicates}건 스킵` : ''}`);
      onImported?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="import-modal">
      <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-black mb-3">지난 방송 상품 불러오기</h3>
        {past.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">불러올 지난 방송이 없습니다.</p>
        ) : (
          <>
            <select value={pick} onChange={(e) => setPick(e.target.value)}
              className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent mb-3"
              data-testid="import-pick">
              {past.map((b) => (
                <option key={b.schedule_id} value={b.schedule_id}>
                  {format(new Date(b.scheduled_at), 'M월 d일 HH:mm', { locale: ko })} · {b.title}
                </option>
              ))}
            </select>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
              <span>가져올 상품 선택 ({selected.size}/{srcItems.filter((it) => !existingIds.has(it.product_id)).length})</span>
              <button onClick={() => setSelected(new Set(srcItems.filter((it) => !existingIds.has(it.product_id)).map((it) => it.product_id)))}
                className="text-xs font-bold text-[#FF3B30]" data-testid="import-select-all">전체 선택</button>
            </div>
            <div className="flex-1 overflow-y-auto -mx-2 px-2 mb-3">
              {srcItems.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">이 방송에 등록된 상품이 없습니다.</p>
              ) : (
                <ul className="space-y-1">
                  {srcItems.map((it) => {
                    const dup = existingIds.has(it.product_id);
                    return (
                      <li key={it.product_id} className={`flex items-center gap-2 p-2 rounded-lg border ${dup ? 'opacity-40 border-black/5' : 'border-black/5 dark:border-white/10'}`} data-testid={`import-row-${it.product_id}`}>
                        <input type="checkbox" checked={selected.has(it.product_id)} disabled={dup}
                          onChange={() => !dup && toggle(it.product_id)} data-testid={`import-check-${it.product_id}`} />
                        <SafeImg src={it.image} kind="product" className="w-10 h-10 rounded object-cover" alt="" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold truncate">{it.name}</p>
                          <p className="text-[10px] text-gray-500">{won(it.sale_price)}{dup ? ' · 이미 오늘 등록됨' : ''}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
        <div className="flex gap-2">
          <button disabled={!pick || loading || selected.size === 0} onClick={doImport}
            className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40"
            data-testid="import-confirm">{loading ? '가져오는 중…' : `${selected.size}건 가져오기`}</button>
          <button onClick={onClose} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold">취소</button>
        </div>
      </div>
    </div>
  );
};

// ---- Add product picker (choose from seller's all products) --------------
// STEP LIVE-STUDIO-PC-PRODUCT-SELECT-UI-1 (2026-10-08) — AddProductModal 을
// shared file (`@/components/seller/live/AddProductPicker`) 로 이동.  LIVE Console
// 과 PC Studio 양쪽에서 import 하여 재사용 (중복 구현 금지 지시 준수).  behavior
// / endpoints / testIds / density presets 모두 **100% 동일** (단순 파일 이동).
import AddProductModal from '@/components/seller/live/AddProductPicker';
// 아래 내용은 이전 inline 구현의 placeholder (unused · search_replace 범위를 좁히기
// 위해 유지되는 scaffolding).  Actual component 는 shared file 에서 import 됨.
const _REMOVED_INLINE_PICKER_MARKER = 'moved-to-AddProductPicker.jsx';
const _PICKER_DENSITY_STORAGE_KEY_UNUSED = 'livemoa_live_product_picker_density';

// ---- 🎥 YouTube Live Connect Panel (Phase 8) -----------------------------
const YT_STATUS_META = {
  connected: { label: '연결됨', cls: 'bg-emerald-500/15 text-emerald-600', dot: 'bg-emerald-500', emoji: '🟢' },
  stopped:   { label: '연결안됨', cls: 'bg-gray-500/10 text-gray-500', dot: 'bg-gray-400', emoji: '⚪' },
  ended:     { label: '방송 종료', cls: 'bg-zinc-500/10 text-zinc-500', dot: 'bg-zinc-400', emoji: '⚪' },
  error:     { label: '오류', cls: 'bg-red-500/10 text-red-500', dot: 'bg-red-500', emoji: '⚠️' },
};

const YouTubeConnectPanel = ({ scheduleId }) => {
  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(null);
  const [candidateCount, setCandidateCount] = useState(0);
  const [oauthConnected, setOauthConnected] = useState(false);
  const [sellerChannel, setSellerChannel] = useState(null);
  const [autoDetect, setAutoDetect] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [mockComment, setMockComment] = useState('');
  const [mockAuthor, setMockAuthor] = useState('MockViewer');
  const [busy, setBusy] = useState(false);
  const [showCredInfo, setShowCredInfo] = useState(false);

  const refresh = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const r = await api.get(`/seller/broadcasts/${scheduleId}/youtube/status`);
      setSession(r.data?.session || null);
      setConfig(r.data?.capabilities || null);
      setOauthConnected(!!r.data?.seller_oauth_connected);
      setSellerChannel(r.data?.seller_channel || null);
      setAutoDetect(r.data?.auto_detect || null);
      // Fetch candidate count sourced from YouTube for this broadcast
      try {
        const q = await api.get(`/seller/broadcasts/${scheduleId}/comment-candidates?q=youtube`);
        const yt = (q.data?.items || []).filter((c) => (c.buyer_id || '').startsWith('youtube:'));
        setCandidateCount(yt.length);
      } catch { /* ignore */ }
    } catch { /* keep last */ }
  }, [scheduleId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  const connect = async (mode) => {
    if (!videoUrl.trim() && mode !== 'mock_quick') {
      toast.error('YouTube URL 또는 Video ID 를 입력하세요.'); return;
    }
    setBusy(true);
    try {
      const body = mode === 'mock_quick'
        ? { video_id: `mock_${Date.now()}` }
        : { video_url: videoUrl.trim() };
      if (mode === 'oauth') body.auth_mode = 'oauth';
      if (mode === 'api_key') body.auth_mode = 'api_key';
      const r = await api.post(`/seller/broadcasts/${scheduleId}/youtube/connect`, body);
      toast.success(`🟢 YouTube 연결됨 · ${r.data.video_title || r.data.video_id} (${r.data.auth_mode})`);
      setVideoUrl('');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '연결 실패'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('YouTube Live 연결을 해제하시겠습니까?')) return;
    setBusy(true);
    try {
      await api.post(`/seller/broadcasts/${scheduleId}/youtube/disconnect`, {});
      toast.success('YouTube 연결이 해제되었습니다.');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '해제 실패'); }
    finally { setBusy(false); }
  };

  const inject = async () => {
    if (!mockComment.trim()) { toast.error('댓글을 입력하세요.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/seller/broadcasts/${scheduleId}/youtube/mock-inject`,
        { comment: mockComment.trim(), author: mockAuthor || 'MockViewer',
          author_id: `mock_${mockAuthor || 'viewer'}` });
      const intent = r.data?.result?.intent || 'create';
      const status = r.data?.result?.candidate?.status;
      toast.success(`YouTube 댓글 → ${intent} · ${status}`);
      setMockComment('');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '주입 실패'); }
    finally { setBusy(false); }
  };

  const startOAuth = async () => {
    try {
      const r = await api.get(`/youtube/oauth/start?schedule_id=${scheduleId}`);
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'OAuth 시작 실패'); }
  };
  const oauthDisconnect = async () => {
    if (!window.confirm('연결된 YouTube 계정을 해제하시겠습니까?')) return;
    try {
      await api.post('/seller/youtube/oauth/disconnect', {});
      toast.success('YouTube 계정 연결 해제됨.');
      refresh();
    } catch (e) { toast.error('OAuth 해제 실패'); }
  };

  // Phase Prod-3 · YouTube LIVE 자동감지 ON/OFF
  const toggleAutoDetect = async () => {
    if (!oauthConnected) { toast.error('먼저 YouTube 계정을 연결하세요.'); return; }
    const next = !(autoDetect?.enabled);
    try {
      await api.patch('/seller/youtube/auto-detect', { enabled: next });
      toast.success(next ? '🟢 LIVE 자동 감지 ON (5분 주기)' : '⚪ LIVE 자동 감지 OFF');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '자동 감지 설정 실패'); }
  };
  const autoDetectNow = async () => {
    if (!autoDetect?.enabled) { toast.error('자동 감지가 OFF 입니다.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/seller/youtube/auto-detect/tick', {});
      const st = r.data?.status;
      if (st === 'live_found') toast.success(`🟢 LIVE 발견! video=${r.data.video_id}`);
      else if (st === 'no_live') toast.info('현재 진행 중인 YouTube LIVE 없음.');
      else if (st === 'just_ended') toast.info('⚪ 방송이 종료되었습니다.');
      else if (st === 'no_target') toast.warning('자동감지할 방송을 먼저 선택해주세요.');
      else if (st === 'no_products') toast.error('자동감지 대상 방송에 판매상품을 먼저 등록해주세요.');
      else if (st === 'conflicting') toast.warning('다른 video 로 이미 연결된 방송입니다.');
      else if (st === 'error') toast.error(`오류: ${r.data.error_code || 'unknown'}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '감지 실패'); }
    finally { setBusy(false); }
  };

  const status = session?.status || 'stopped';
  const meta = YT_STATUS_META[status] || YT_STATUS_META.stopped;
  const isMock = session?.auth_mode === 'mock';
  const lastPolled = session?.last_polled_at ? new Date(session.last_polled_at) : null;
  const lastMsg = session?.last_message_at ? new Date(session.last_message_at) : null;
  const showMockControls = isMock && status === 'connected';
  const isReal = config?.real_mode_ready && !isMock;

  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid="youtube-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-black inline-flex items-center gap-2 flex-wrap">
          <Youtube size={14} className="text-red-500" /> YouTube Live 댓글 연동
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${meta.cls}`} data-testid="yt-status-badge">
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} ${status === 'connected' ? 'animate-pulse' : ''}`} />
            {meta.emoji} {meta.label}{status === 'connected' ? ' (세션)' : ''}
          </span>
          {isMock && status === 'connected' && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-orange-500/10 text-orange-600" data-testid="yt-mock-badge">MOCK</span>
          )}
          {/* Phase Prod-2 · OAuth 계정(진짜 Google 로그인) 상태를 세션과 분리해 항상 표시 */}
          {oauthConnected ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/10 text-emerald-600" data-testid="yt-oauth-badge">
              {sellerChannel?.channel_thumbnail && (
                <img src={sellerChannel.channel_thumbnail} alt="" className="w-3 h-3 rounded-full object-cover" />
              )}
              ● OAuth · {sellerChannel?.channel_title || '연결됨'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-gray-500/10 text-gray-500" data-testid="yt-oauth-badge">
              ○ OAuth 미연결
            </span>
          )}
        </p>
        <button onClick={() => setShowCredInfo((v) => !v)} className="text-[10px] font-bold text-gray-500 hover:text-black dark:hover:text-white underline"
          data-testid="yt-cred-toggle">
          {showCredInfo ? '자격증명 접기' : '자격증명 상태 보기'}
        </button>
      </div>

      {/* Phase 8-1: credential status table (masked) */}
      {showCredInfo && config && (
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 text-[11px] space-y-1 bg-black/[0.02] dark:bg-white/[0.02]" data-testid="yt-cred-panel">
          <div className="flex justify-between"><span className="text-gray-500">API Key</span>
            <span className={`font-bold ${config.api_key_available ? 'text-emerald-600' : 'text-gray-400'}`}
              data-testid="yt-cred-apikey">
              {config.api_key_available ? `✅ 준비됨 (${config.api_key_hint})` : '❌ 미설정'}
            </span></div>
          <div className="flex justify-between"><span className="text-gray-500">OAuth Client</span>
            <span className={`font-bold ${config.oauth_available ? 'text-emerald-600' : 'text-gray-400'}`}
              data-testid="yt-cred-oauth">
              {config.oauth_available ? `✅ 준비됨 (${config.oauth_client_id_hint})` : '❌ 미설정'}
            </span></div>
          <div className="flex justify-between"><span className="text-gray-500">Mock Mode</span>
            <span className={`font-bold ${config.mock_mode ? 'text-orange-600' : 'text-emerald-600'}`}
              data-testid="yt-cred-mock">
              {config.mock_mode ? 'ON' : 'OFF'}
            </span></div>
          <div className="flex justify-between"><span className="text-gray-500">실제 댓글 수신</span>
            <span className={`font-bold ${isReal ? 'text-emerald-600' : 'text-orange-600'}`}
              data-testid="yt-cred-realmode">
              {isReal ? '✅ 준비됨' : '⏳ Mock 사용 중'}
            </span></div>
          <div className="flex justify-between text-[10px] pt-1 border-t border-black/5 dark:border-white/5">
            <span className="text-gray-500">Redirect URI</span>
            <span className="font-mono text-gray-600 dark:text-gray-300 truncate max-w-[240px]" title={config.redirect_uri}>{config.redirect_uri}</span>
          </div>
        </div>
      )}

      {/* Phase Prod-3 · YouTube LIVE 자동감지 (OAuth 연결 시에만 표시) */}
      {oauthConnected && (
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 bg-black/[0.02] dark:bg-white/[0.02] text-xs" data-testid="yt-autodetect-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="font-black text-[11px] inline-flex items-center gap-1">
                🤖 YouTube LIVE 자동 감지
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${autoDetect?.enabled ? 'bg-emerald-500/10 text-emerald-600' : 'bg-gray-500/10 text-gray-500'}`}
                  data-testid="yt-autodetect-badge">
                  {autoDetect?.enabled ? 'ON' : 'OFF'}
                </span>
              </p>
              {autoDetect?.enabled && (() => {
                const last = autoDetect?.last;
                const st = last?.status;
                if (!last) return <p className="text-[10px] text-gray-500 mt-0.5">첫 검색 대기 중…</p>;
                if (st === 'live_found') return (
                  <p className="text-[10px] text-emerald-600 mt-0.5" data-testid="yt-autodetect-status">
                    🟢 LIVE 발견 · {last.channel_title || 'channel'} · Video ID: <span className="font-mono">{last.video_id}</span>
                    {last.action === 'created' && ' · 세션 자동 생성됨'}
                    {last.action === 'reused_active' && ' · 기존 세션 재사용'}
                  </p>
                );
                if (st === 'no_live') return (
                  <p className="text-[10px] text-gray-500 mt-0.5" data-testid="yt-autodetect-status">
                    ⚪ 자동 감지 대기 중 · 현재 진행 중인 YouTube LIVE 없음 (5분 주기)
                  </p>
                );
                if (st === 'just_ended') return (
                  <p className="text-[10px] text-gray-500 mt-0.5" data-testid="yt-autodetect-status">
                    ⚪ 방송이 종료되었습니다{last.video_title && <> · {last.video_title}</>} · 새 LIVE 감지 대기 중
                  </p>
                );
                if (st === 'no_target') return (
                  <p className="text-[10px] text-orange-600 mt-0.5" data-testid="yt-autodetect-status">
                    🟠 자동감지할 방송을 먼저 선택해주세요. (판매자 대시보드 → 방송 관리)
                  </p>
                );
                if (st === 'no_products') return (
                  <p className="text-[10px] text-red-500 mt-0.5" data-testid="yt-autodetect-status">
                    🔴 자동감지 대상 방송에 판매상품을 먼저 등록해주세요.
                    {last.video_title && <> · {last.video_title}</>}
                  </p>
                );
                if (st === 'conflicting') return (
                  <p className="text-[10px] text-orange-600 mt-0.5" data-testid="yt-autodetect-status">
                    🟠 다른 video 로 이미 연결된 방송입니다 (덮어쓰기 방지)
                  </p>
                );
                if (st === 'error') return (
                  <p className="text-[10px] text-red-500 mt-0.5" data-testid="yt-autodetect-status">
                    🟠 자동 감지 확인 필요: {last.error_code || 'unknown'}
                  </p>
                );
                return <p className="text-[10px] text-gray-500 mt-0.5">상태: {st}</p>;
              })()}
              {!autoDetect?.enabled && (
                <p className="text-[10px] text-gray-500 mt-0.5">
                  ON 시 5분 주기로 채널의 LIVE 를 자동 검색 (quota 1 unit/회). LIVE 발견 후 60초 주기로 단축.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {autoDetect?.enabled && (
                <button onClick={autoDetectNow} disabled={busy}
                  className="px-2 py-1 rounded-full text-[10px] font-bold border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10"
                  data-testid="yt-autodetect-now">지금 검색</button>
              )}
              <label className="relative inline-block w-10 h-6">
                <input type="checkbox" className="peer opacity-0 w-0 h-0"
                  checked={!!autoDetect?.enabled}
                  onChange={toggleAutoDetect}
                  data-testid="yt-autodetect-switch"/>
                <span className="absolute inset-0 rounded-full bg-black/10 peer-checked:bg-emerald-500 transition-colors cursor-pointer"/>
                <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow peer-checked:translate-x-4 transition-transform pointer-events-none"/>
              </label>
            </div>
          </div>
        </div>
      )}

      {session && status !== 'stopped' && status !== 'ended' && (
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 bg-black/[0.02] dark:bg-white/[0.02] text-xs space-y-1" data-testid="yt-session-card">
          <p className="font-bold truncate inline-flex items-center gap-1">
            <Link2 size={11} /> {session.video_title || session.video_id}
          </p>
          <p className="text-gray-500">
            채널: <span className="font-mono">{session.channel_title || session.channel_id || '-'}</span>
            {' · '}Video ID: <span className="font-mono">{session.video_id}</span>
          </p>
          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="text-center">
              <p className="text-[9px] uppercase text-gray-400">최근 댓글</p>
              <p className="text-xs font-black">{lastMsg ? format(lastMsg, 'HH:mm:ss', { locale: ko }) : '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-[9px] uppercase text-gray-400">수신 댓글</p>
              <p className="text-xs font-black" data-testid="yt-msg-count">{session.message_count || 0}건</p>
            </div>
            <div className="text-center">
              <p className="text-[9px] uppercase text-gray-400">주문 후보</p>
              <p className="text-xs font-black" data-testid="yt-cand-count">{candidateCount}건</p>
            </div>
          </div>
          <p className="text-[10px] text-gray-400">
            폴링 {session.poll_count || 0}회 · 중복 {session.dedup_count || 0}건 무시 · 간격 {(session.poll_interval_ms || 5000) / 1000}s
            {lastPolled && <> · 최근 폴링 {format(lastPolled, 'HH:mm:ss', { locale: ko })}</>}
          </p>
          {status === 'error' && session.error_message && (
            <p className="text-red-500 inline-flex items-center gap-1"><AlertTriangle size={11} /> {session.error_message}</p>
          )}
          <div className="pt-2 flex gap-2 flex-wrap">
            <button onClick={disconnect} disabled={busy}
              className="px-3 py-1 rounded-full text-[11px] font-bold border border-red-500/40 text-red-500 inline-flex items-center gap-1"
              data-testid="yt-disconnect-btn"><WifiOff size={11} /> 연결 해제</button>
          </div>
        </div>
      )}

      {status === 'ended' && session && (
        <div className="rounded-xl border border-zinc-500/30 p-3 bg-zinc-500/5 text-xs" data-testid="yt-ended-card">
          <p className="font-bold text-zinc-600 dark:text-zinc-300">⚪ 방송이 종료되었습니다.</p>
          <p className="text-[11px] text-gray-500 mt-1">
            수신된 댓글 {session.message_count || 0}건, 주문 후보 {candidateCount}건은 그대로 보존됩니다.
          </p>
        </div>
      )}

      {status !== 'connected' && (
        <div className="space-y-2" data-testid="yt-connect-form">
          <div className="flex gap-2 flex-wrap">
            <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="YouTube Live URL 또는 Video ID (예: https://youtube.com/watch?v=xxx)"
              className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
              data-testid="yt-url-input" />
            <button onClick={() => connect()} disabled={busy || !videoUrl.trim()}
              className="px-4 py-1.5 rounded-full text-xs font-bold bg-red-500 text-white disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="yt-connect-btn"><Wifi size={11} /> 연결</button>
          </div>
          {config?.oauth_available && (
            <div className="text-[11px] text-gray-500 inline-flex items-center gap-2 flex-wrap" data-testid="yt-oauth-row">
              {oauthConnected ? (
                <>
                  {sellerChannel?.channel_thumbnail && (
                    <img src={sellerChannel.channel_thumbnail} alt="channel"
                      className="w-6 h-6 rounded-full border border-black/10 dark:border-white/10 object-cover"
                      data-testid="yt-channel-thumb" />
                  )}
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-bold" data-testid="yt-oauth-connected">
                    ● 연결됨
                  </span>
                  {sellerChannel?.channel_title && (
                    <span className="font-bold text-gray-700 dark:text-gray-200 truncate max-w-[200px]"
                      title={sellerChannel.channel_title}
                      data-testid="yt-channel-title">
                      {sellerChannel.channel_title}
                    </span>
                  )}
                  <button onClick={oauthDisconnect} className="underline hover:text-red-500" data-testid="yt-oauth-disconnect">연결 해제</button>
                </>
              ) : (
                <button onClick={startOAuth} className="underline text-blue-600 hover:text-red-500" data-testid="yt-oauth-start">
                  👤 YouTube 계정으로 직접 연결하기 (비공개 방송도 지원)
                </button>
              )}
            </div>
          )}
          {config?.mock_mode && (
            <button onClick={() => connect('mock_quick')} disabled={busy}
              className="text-[11px] font-bold text-orange-600 hover:text-red-500"
              data-testid="yt-mock-connect">
              ⚙️ Mock 세션 즉시 시작 (자격증명 없이 파이프라인 테스트)
            </button>
          )}
        </div>
      )}

      {showMockControls && (
        <div className="rounded-xl border border-dashed border-orange-500/40 p-3 space-y-2 bg-orange-500/5" data-testid="yt-mock-inject">
          <p className="text-[11px] font-bold text-orange-700 dark:text-orange-400">
            🧪 Mock 시청자 댓글 주입 — 실제 YouTube 자격증명 없이 파이프라인을 검증합니다.
          </p>
          <div className="flex gap-2 flex-wrap">
            <input value={mockAuthor} onChange={(e) => setMockAuthor(e.target.value)}
              placeholder="시청자 이름" className="w-32 px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
              data-testid="yt-mock-author" />
            <input value={mockComment} onChange={(e) => setMockComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && inject()}
              placeholder="예: 4번 블랙 M 2개 · 4번 1개로 변경 · 4번 취소"
              className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
              data-testid="yt-mock-comment" />
            <button onClick={inject} disabled={busy}
              className="px-3 py-1.5 rounded-full text-xs font-bold bg-orange-500 text-white disabled:opacity-40"
              data-testid="yt-mock-send">주입</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- 🎬 TikTok Live Panel (Phase 14-T · Mock Adapter) ---------------------
// UI mirror of YouTubeConnectPanel. Backend routes the injected comment
// through the SAME `_co_process_incoming` engine so no order-flow logic is
// duplicated here.
const TikTokConnectPanel = ({ scheduleId }) => {
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState(null);
  const [candidateCount, setCandidateCount] = useState(0);
  const [uniqueId, setUniqueId] = useState('');
  const [mockComment, setMockComment] = useState('');
  const [mockAuthor, setMockAuthor] = useState('MockTikTokViewer');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const [statusRes, cfgRes, listRes] = await Promise.all([
        api.get(`/seller/broadcasts/${scheduleId}/tiktok/status`),
        api.get('/tiktok/config'),
        api.get(`/seller/broadcasts/${scheduleId}/comment-candidates?q=tiktok`).catch(() => ({ data: { items: [] } })),
      ]);
      setSession(statusRes.data?.session || null);
      setConfig(cfgRes.data || null);
      const tt = (listRes.data?.items || []).filter((c) => (c.buyer_id || '').startsWith('tiktok:'));
      setCandidateCount(tt.length);
    } catch { /* keep last */ }
  }, [scheduleId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  const connect = async () => {
    setBusy(true);
    try {
      const body = uniqueId.trim() ? { unique_id: uniqueId.trim() } : {};
      const r = await api.post(`/seller/broadcasts/${scheduleId}/tiktok/connect`, body);
      toast.success(`🎬 TikTok Mock 세션 시작 · @${r.data?.session?.unique_id}`);
      setUniqueId('');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '연결 실패'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('TikTok 세션을 해제하시겠습니까?')) return;
    setBusy(true);
    try {
      await api.post(`/seller/broadcasts/${scheduleId}/tiktok/disconnect`, {});
      toast.success('TikTok 세션 해제됨');
      refresh();
    } catch { toast.error('해제 실패'); }
    finally { setBusy(false); }
  };

  const inject = async () => {
    if (!mockComment.trim()) { toast.error('댓글을 입력하세요.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/seller/broadcasts/${scheduleId}/tiktok/mock-inject`,
        { comment: mockComment.trim(), author: mockAuthor || 'MockTikTokViewer',
          author_id: `mock_${mockAuthor || 'viewer'}` });
      const intent = r.data?.result?.intent || 'create';
      const status = r.data?.result?.candidate?.status;
      toast.success(`TikTok 댓글 → ${intent} · ${status}`);
      setMockComment('');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '주입 실패'); }
    finally { setBusy(false); }
  };

  const connected = session?.status === 'connected';

  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 space-y-3 bg-white dark:bg-[#141414]" data-testid="tiktok-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-black text-white text-xs font-black">tt</span>
          <div>
            <p className="font-black text-sm">TikTok LIVE 댓글 <span className="text-[10px] font-black bg-orange-500 text-white px-1.5 py-0.5 rounded-full ml-1">TEST MODE</span></p>
            <p className="text-[10px] text-gray-500">
              후보 <b>{candidateCount}</b>건 · {connected ? `@${session?.unique_id || 'mock'} 연결됨` : '미연결'}
            </p>
          </div>
        </div>
        {connected ? (
          <button onClick={disconnect} disabled={busy}
            className="px-3 py-1.5 rounded-full text-xs font-bold border border-black/10 dark:border-white/10"
            data-testid="tiktok-disconnect">세션 해제</button>
        ) : (
          <button onClick={connect} disabled={busy}
            className="px-3 py-1.5 rounded-full text-xs font-bold bg-black text-white"
            data-testid="tiktok-connect">Mock 세션 시작</button>
        )}
      </div>

      {config && !config.official_api_available && (
        <p className="text-[10px] text-gray-500 leading-relaxed">
          {config.notice || 'TikTok 공식 LIVE 댓글 API 미제공. 테스트 모드로만 제공됩니다.'}
        </p>
      )}

      {!connected && (
        <div className="flex gap-2">
          <input value={uniqueId} onChange={(e) => setUniqueId(e.target.value)}
            placeholder="TikTok Unique ID (예: yourshop)  — 비워두면 자동" data-testid="tiktok-unique-id"
            className="flex-1 px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
        </div>
      )}

      {connected && (
        <div className="rounded-xl border border-dashed border-pink-500/40 p-3 space-y-2 bg-pink-500/5" data-testid="tiktok-mock-inject">
          <p className="text-[11px] font-bold text-pink-700 dark:text-pink-400">
            🧪 Mock 시청자 댓글 주입 — YouTube 와 동일한 주문 파이프라인으로 흐릅니다.
          </p>
          <div className="flex gap-2 flex-wrap">
            <input value={mockAuthor} onChange={(e) => setMockAuthor(e.target.value)}
              placeholder="시청자 이름" className="w-32 px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
              data-testid="tiktok-mock-author" />
            <input value={mockComment} onChange={(e) => setMockComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && inject()}
              placeholder="예: 1번 블랙 M 2개 · 1번 취소"
              className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
              data-testid="tiktok-mock-comment" />
            <button onClick={inject} disabled={busy}
              className="px-3 py-1.5 rounded-full text-xs font-bold bg-pink-500 text-white disabled:opacity-40"
              data-testid="tiktok-mock-send">주입</button>
          </div>
        </div>
      )}
    </div>
  );
};




// ---- 💬 Comment Order Panel (Phase 7) --------------------------------------
const STATUS_META = {
  pending:      { label: '주문진행', cls: 'bg-emerald-500/10 text-emerald-600' },
  needs_option: { label: '옵션필요', cls: 'bg-orange-500/10 text-orange-600' },
  needs_review: { label: '확인 필요', cls: 'bg-amber-500/10 text-amber-700' },
  confirmed:    { label: '주문완료', cls: 'bg-[#FF3B30]/10 text-[#FF3B30]' },
  cancelled:    { label: '취소',    cls: 'bg-gray-500/10 text-gray-500' },
  expired:      { label: '만료',    cls: 'bg-zinc-500/10 text-zinc-500' },
};

const TABS = [
  { key: 'pending',      label: '신규' },
  { key: 'needs_option', label: '옵션필요' },
  { key: 'needs_review', label: '확인 필요' },
  { key: 'confirmed',    label: '주문완료' },
  { key: 'terminal',     label: '취소·만료' },
];

// Phase 14-T · Platform filter tabs — client-side against Candidate.source.
// Backend list endpoint is untouched; we filter the already-fetched `rows` so
// the counts stay in-sync with whatever status/search filter is active.
const PLATFORM_TABS = [
  { key: 'all',      label: '전체',     testid: 'plat-all' },
  { key: 'youtube',  label: 'YouTube',  testid: 'plat-youtube' },
  { key: 'tiktok',   label: 'TikTok',   testid: 'plat-tiktok' },
  { key: 'internal', label: '내부테스트', testid: 'plat-internal' },
];
const matchesPlatform = (source, key) => {
  const s = (source || '').toLowerCase();
  if (key === 'all') return true;
  if (key === 'youtube') return s.startsWith('youtube');
  if (key === 'tiktok') return s.startsWith('tiktok');
  if (key === 'internal') return s === '' || s === 'internal-test' || s === 'none' || s.includes('mock') && !s.startsWith('youtube') && !s.startsWith('tiktok');
  return true;
};
const platformOfSource = (source) => {
  const s = (source || '').toLowerCase();
  if (s.startsWith('youtube')) return 'youtube';
  if (s.startsWith('tiktok')) return 'tiktok';
  return 'internal';
};

const CANCEL_REASONS = [
  { key: 'stock_out', label: '재고 소진' },
  { key: 'buyer_no_response', label: '구매자 무응답' },
  { key: 'duplicate', label: '중복 주문' },
  { key: 'invalid', label: '잘못된 댓글' },
  { key: 'other', label: '기타' },
];

const HISTORY_META = {
  created: { label: '생성', color: 'text-emerald-600' },
  modified_by_buyer: { label: '구매자 수정', color: 'text-blue-600' },
  cancelled_by_buyer: { label: '구매자 취소', color: 'text-orange-600' },
  cancelled_by_seller: { label: '판매자 취소', color: 'text-orange-600' },
  buyer_edited: { label: '확인페이지 편집', color: 'text-blue-500' },
  confirmed: { label: '장바구니 담김', color: 'text-[#FF3B30]' },
  order_placed: { label: '주문 접수', color: 'text-[#FF3B30]' },
  expired: { label: '자동 만료', color: 'text-zinc-500' },
};

// ---- Candidate detail + history modal ----
const CandidateDetailModal = ({ candidateId, open, onClose }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !candidateId) return;
    setLoading(true);
    api.get(`/seller/comment-candidates/${candidateId}/history`)
      .then((r) => setPayload(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || '내역을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [open, candidateId]);
  if (!open) return null;
  const c = payload?.candidate;
  const hist = payload?.history || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="candidate-detail-modal">
      <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-black inline-flex items-center gap-1"><History size={14} /> 댓글 주문 상세</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10"><X size={14} /></button>
        </div>
        {loading || !c ? (
          <p className="text-xs text-gray-400 py-6 text-center">불러오는 중…</p>
        ) : (
          <>
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-full bg-black text-white text-xs font-black flex items-center justify-center">{c.slot_number}</span>
                <p className="text-sm font-bold truncate flex-1">{c.product_name}</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_META[c.status]?.cls || ''}`}>{STATUS_META[c.status]?.label || c.status}</span>
              </div>
              <p className="text-[11px] text-gray-500 mb-1">💬 "{c.original_comment}"</p>
              <p className="text-[11px] text-gray-500">
                {c.buyer_name} · 옵션 {Object.entries(c.selected_options || {}).map(([, v]) => v).join(' / ') || '없음'} · {c.quantity}개 · {won(c.unit_price)}
              </p>
              {c.cancel_reason && (
                <p className="text-[11px] text-orange-600 mt-1">취소 사유: {c.cancel_reason}{c.cancel_note ? ` · ${c.cancel_note}` : ''}</p>
              )}
              {c.order_id && (
                <p className="text-[11px] text-[#FF3B30] font-bold mt-1">
                  주문번호: <Link to={`/orders/${c.order_id}`} target="_blank" className="underline" data-testid="detail-order-link">
                    {c.order_number || c.order_id} <ExternalLink size={10} className="inline" />
                  </Link>
                </p>
              )}
            </div>
            <p className="text-[11px] font-black mb-2 text-gray-600 dark:text-gray-300">변경 히스토리 · {hist.length}건</p>
            <ol className="space-y-2" data-testid="detail-history-list">
              {hist.length === 0 && <li className="text-xs text-gray-400 text-center py-4">히스토리가 없습니다.</li>}
              {hist.map((h) => {
                const m = HISTORY_META[h.action] || { label: h.action, color: 'text-gray-500' };
                const when = new Date(h.created_at);
                return (
                  <li key={h.history_id} className="text-[11px] pl-3 border-l-2 border-black/10 dark:border-white/10" data-testid={`history-${h.action}`}>
                    <p className={`font-black ${m.color}`}>{m.label}</p>
                    <p className="text-gray-500">{format(when, 'M월 d일 HH:mm:ss', { locale: ko })}</p>
                    {h.extra?.comment && <p className="text-gray-700 dark:text-gray-300">"{h.extra.comment}"</p>}
                    {h.after && (
                      <p className="text-gray-400">
                        {h.after.status && `상태 → ${STATUS_META[h.after.status]?.label || h.after.status}`}
                        {typeof h.after.quantity === 'number' && ` · 수량 ${h.after.quantity}개`}
                        {h.after.reason && ` · 사유 ${h.after.reason}`}
                        {h.after.order_number && ` · 주문 ${h.after.order_number}`}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    </div>
  );
};

// ---- Cancel with reason picker ----
const CancelReasonModal = ({ open, onClose, onConfirm }) => {
  const [reason, setReason] = useState('stock_out');
  const [note, setNote] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="cancel-reason-modal">
      <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-black mb-3">주문 후보 취소</h3>
        <p className="text-[11px] text-gray-500 mb-3">취소 사유를 선택하세요. 히스토리에 기록됩니다.</p>
        <div className="space-y-1 mb-3">
          {CANCEL_REASONS.map((r) => (
            <label key={r.key} className="flex items-center gap-2 p-2 rounded-lg border border-black/10 dark:border-white/10 text-xs">
              <input type="radio" name="cancel-reason" value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)}
                data-testid={`cancel-reason-${r.key}`} />
              <span className="font-bold">{r.label}</span>
            </label>
          ))}
        </div>
        <label className="block text-[11px] font-bold mb-1 text-gray-500">추가 메모 (선택)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 재고 부족으로 취소"
          className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-xs bg-transparent mb-3"
          data-testid="cancel-reason-note" />
        <div className="flex gap-2">
          <button onClick={() => onConfirm(reason, note)}
            className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-black"
            data-testid="cancel-reason-confirm">취소 확정</button>
          <button onClick={onClose}
            className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-black">닫기</button>
        </div>
      </div>
    </div>
  );
};

const CommentOrderPanel = ({ scheduleId }) => {
  const [text, setText] = useState('');
  const [buyer, setBuyer] = useState('테스터');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('pending');
  const [platform, setPlatform] = useState('all');   // Phase 14-T · client-side platform filter
  const [query, setQuery] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  const [showOld, setShowOld] = useState(false);

  // Phase 15-B follow-up · Comment Reply Toggle.
  //   `notifDefault`  = the persisted `comment_order_confirmed` from
  //                     /seller/notifications/settings; read-only reference.
  //   `masterEnabled` = master alimtalk switch; when OFF the toggle is locked
  //                     off since the dispatcher will always drop to
  //                     skipped_disabled(master_off) anyway.
  //   `sendNotify`    = LIVE-SESSION-ONLY override. `null` means "follow the
  //                     seller default"; a bool means the seller explicitly
  //                     flipped it in this session. Never persisted server-side.
  const [notifDefault, setNotifDefault] = useState(true);
  const [masterEnabled, setMasterEnabled] = useState(true);
  const [sendNotify, setSendNotify] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/seller/notifications/settings').then((r) => {
      if (!alive) return;
      setNotifDefault(!!r.data?.comment_order_confirmed);
      setMasterEnabled(r.data?.kakao_enabled !== false);
    }).catch(() => {});
    return () => { alive = false; };
  }, [scheduleId]);
  const effectiveOn = sendNotify == null ? notifDefault : sendNotify;
  const toggleDisabled = !masterEnabled;

  // Track the most-recently seen created_at so we can toast when a new
  // candidate appears via the 3s polling loop. Uses a ref so the setter
  // stays stable across re-renders and doesn't retrigger effects.
  const seenLatestRef = React.useRef(null);
  const firstLoadRef = React.useRef(true);

  const refresh = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const params = new URLSearchParams();
      if (tab && tab !== 'all') params.set('status', tab);
      if (query.trim()) params.set('q', query.trim());
      const r = await api.get(`/seller/broadcasts/${scheduleId}/comment-candidates?${params}`);
      const items = r.data?.items || [];
      setRows(items);
      setCounts(r.data?.counts || {});
      // ---- Toast on newly-arrived candidates (created_at newer than last seen) ----
      if (!firstLoadRef.current) {
        const fresh = items.filter((c) =>
          seenLatestRef.current ? new Date(c.created_at) > new Date(seenLatestRef.current) : false
        );
        if (fresh.length > 0) {
          const first = fresh[0];
          toast.success(`🔔 새 댓글 주문 · ${first.slot_number}번 · ${first.buyer_name}${fresh.length > 1 ? ` 외 ${fresh.length - 1}건` : ''}`);
        }
      }
      if (items.length > 0) {
        seenLatestRef.current = items[0].created_at; // items sorted desc by created_at
      }
      firstLoadRef.current = false;
    } catch { /* ignore transient errors */ }
  }, [scheduleId, tab, query]);

  useEffect(() => {
    firstLoadRef.current = true;
    seenLatestRef.current = null;
    refresh();
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  const parse = async () => {
    if (!text.trim()) { toast.error('댓글을 입력하세요.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/seller/broadcasts/${scheduleId}/parse-comment`,
        { comment: text.trim(), buyer_name: buyer || '테스터', buyer_id: `test:${buyer}` });
      const intent = r.data.intent || 'create';
      if (r.data.duplicate) toast.info('10초 이내 동일 댓글은 중복 방지됩니다.');
      else if (intent === 'modify') toast.success(`✏️ 수정 처리 · ${r.data.candidate.slot_number}번 · 수량 ${r.data.candidate.quantity}개`);
      else if (intent === 'cancel') toast.info(`🚫 취소 처리 · ${r.data.candidate.slot_number}번`);
      else toast.success(`✅ 후보 생성 · ${r.data.candidate.slot_number}번 · ${STATUS_META[r.data.candidate.status]?.label || r.data.candidate.status}`);
      setText('');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '분석 실패'); }
    finally { setBusy(false); }
  };

  const copyLink = async (cid) => {
    const url = `${window.location.origin}/live-order/${cid}`;
    try { await navigator.clipboard.writeText(url); toast.success('확인 URL 복사됨'); } catch { toast.error(url); }
  };

  const doSellerCancel = async (cid, reason, note) => {
    try {
      await api.post(`/seller/comment-candidates/${cid}/cancel`, { reason, note });
      toast.success('취소 처리되었습니다.');
      setCancelId(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || '취소 실패'); }
  };

  // ---- Auto-fold candidates older than 10 minutes on the current tab -----
  const FOLD_MINUTES = 10;
  // Phase 14-T · platform-aware split. First filter rows down to the selected
  // platform, THEN split into fresh/old for the "10분 이전" collapse UX.
  const platformCounts = useMemo(() => {
    const c = { all: rows.length, youtube: 0, tiktok: 0, internal: 0 };
    rows.forEach((r) => { c[platformOfSource(r.source)] += 1; });
    return c;
  }, [rows]);

  const { fresh, old } = useMemo(() => {
    const cutoff = Date.now() - FOLD_MINUTES * 60 * 1000;
    const f = [];
    const o = [];
    rows.forEach((c) => {
      if (!matchesPlatform(c.source, platform)) return;
      if (new Date(c.created_at).getTime() >= cutoff) f.push(c); else o.push(c);
    });
    return { fresh: f, old: o };
  }, [rows, platform]);

  const pendingCount = (counts.pending || 0) + (counts.needs_option || 0);

  const renderRow = (c) => {
    const meta = STATUS_META[c.status] || { label: c.status, cls: 'bg-black/5 text-black' };
    const optStr = Object.entries(c.selected_options || {}).map(([, v]) => `${v}`).join(' / ') || '옵션 없음';
    const terminal = c.status === 'confirmed' || c.status === 'cancelled' || c.status === 'expired';
    return (
      <div key={c.candidate_id}
        className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-black/10 dark:border-white/10"
        data-testid={`candidate-${c.candidate_id}`}>
        <SafeImg src={c.image} kind="product" className="w-10 h-10 rounded object-cover" alt="" />
        <div className="flex-1 min-w-[160px] text-xs">
          <p className="font-bold truncate">
            {(c.source || '').startsWith('youtube') && (
              <span className="text-[9px] font-black text-red-500 bg-red-500/10 px-1.5 py-0.5 rounded-full mr-1 align-middle" data-testid={`cand-plat-yt-${c.candidate_id}`}>YouTube</span>
            )}
            {(c.source || '').startsWith('tiktok') && (
              <span className="text-[9px] font-black text-white bg-black px-1.5 py-0.5 rounded-full mr-1 align-middle" data-testid={`cand-plat-tt-${c.candidate_id}`}>TikTok</span>
            )}
            {(c.source || '').startsWith('instagram') && (
              <span className="text-[9px] font-black text-white bg-gradient-to-r from-fuchsia-500 to-orange-400 px-1.5 py-0.5 rounded-full mr-1 align-middle" data-testid={`cand-plat-ig-${c.candidate_id}`}>Instagram</span>
            )}
            {!(c.source || '').startsWith('youtube') && !(c.source || '').startsWith('tiktok') && !(c.source || '').startsWith('instagram') && (
              <span className="text-[9px] font-black text-gray-500 bg-gray-500/10 px-1.5 py-0.5 rounded-full mr-1 align-middle" data-testid={`cand-plat-int-${c.candidate_id}`}>내부</span>
            )}
            {c.buyer_name} · "{c.original_comment}"
          </p>
          <p className="text-gray-500 truncate">
            <span className="font-bold text-black dark:text-white">{c.slot_number}번 · {c.product_name}</span>
            {' · '}{optStr}{' · '}{c.quantity}개{' · '}{won(c.unit_price)}
          </p>
          {c.order_id && (
            <Link to={`/orders/${c.order_id}`} target="_blank"
              className="inline-flex items-center gap-1 text-[10px] font-black text-[#FF3B30] hover:underline mt-0.5"
              data-testid={`candidate-order-link-${c.candidate_id}`}>
              주문번호 {c.order_number || c.order_id} <ExternalLink size={10} />
            </Link>
          )}
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black ${meta.cls}`} data-testid={`candidate-status-${c.candidate_id}`}>{meta.label}</span>
        <div className="flex gap-1 ml-auto flex-wrap">
          {!terminal && (
            <button onClick={async () => {
              try {
                // Phase 15-B follow-up · pass the session-only send_notification
                // decision so the backend can override the seller default for
                // THIS confirm only.
                const r = await api.post(`/seller/candidates/${c.candidate_id}/confirm-order`,
                  { send_notification: effectiveOn });
                const url = `${window.location.origin}${r.data.checkout_url}`;
                try { await navigator.clipboard.writeText(url); } catch {}
                toast.success(r.data.reused ? '기존 체크아웃 링크가 열렸습니다.'
                  : (effectiveOn
                      ? '주문확정 · 알림톡(Mock) 전송, 링크 복사 및 새 탭 열림.'
                      : '주문확정 · 알림 미발송(설정 OFF), 링크 복사 및 새 탭 열림.'));
                window.open(url, '_blank', 'noopener');
              } catch (e) { toast.error(e?.response?.data?.detail || '주문확정 실패'); }
            }}
              className="px-2 py-1 text-[10px] font-black bg-[#FF3B30] text-white rounded-full"
              data-testid={`candidate-confirm-order-${c.candidate_id}`}>주문확정</button>
          )}
          {c.seller_cart_id && !c.order_id && (
            <a href={`/checkout/${c.seller_cart_id}`} target="_blank" rel="noreferrer"
              className="px-2 py-1 text-[10px] font-bold border border-[#FF3B30]/40 text-[#FF3B30] rounded-full"
              data-testid={`candidate-open-checkout-${c.candidate_id}`}>체크아웃 열기</a>
          )}
          {/* STEP 7-LIVE-COMMERCE-B · checkout URL 원클릭 복사/공유 · 기존 seller_carts 재사용 */}
          {c.seller_cart_id && !c.order_id && (
            <button type="button"
              onClick={async () => {
                const url = `${window.location.origin}/checkout/${c.seller_cart_id}`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success('구매 링크가 복사되었습니다.');
                } catch { toast.error('링크 복사에 실패했습니다.'); }
              }}
              className="px-2 py-1 text-[10px] font-bold border border-black/10 dark:border-white/10 rounded-full inline-flex items-center gap-1"
              data-testid={`candidate-copy-url-${c.candidate_id}`}>
              <Copy size={10} /> URL 복사
            </button>
          )}
          {c.seller_cart_id && !c.order_id && typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
            <button type="button"
              onClick={async () => {
                const url = `${window.location.origin}/checkout/${c.seller_cart_id}`;
                const productLine = c.product_name ? `\n상품: ${c.product_name}` : '';
                const qtyLine = c.quantity ? `\n수량: ${c.quantity}개` : '';
                const text = `LIVEMOA 주문 확인${productLine}${qtyLine}\n\n구매 링크로 접속해서 주소를 입력해 주세요.`;
                try {
                  await navigator.share({ title: 'LIVEMOA 주문 확인', text, url });
                } catch (e) {
                  if (e?.name !== 'AbortError') {
                    try { await navigator.clipboard.writeText(url); toast.info('공유 취소 · 링크를 복사했습니다.'); }
                    catch { toast.error('공유에 실패했습니다.'); }
                  }
                }
              }}
              className="px-2 py-1 text-[10px] font-bold border border-black/10 dark:border-white/10 rounded-full inline-flex items-center gap-1"
              data-testid={`candidate-share-url-${c.candidate_id}`}>
              <Share2 size={10} /> 공유
            </button>
          )}
          <button onClick={() => setDetailId(c.candidate_id)}
            className="px-2 py-1 text-[10px] font-bold border border-black/10 dark:border-white/10 rounded-full"
            data-testid={`candidate-detail-${c.candidate_id}`}>상세</button>
          {!terminal && (
            <button onClick={() => setCancelId(c.candidate_id)}
              className="px-2 py-1 text-[10px] font-bold border border-red-500/30 text-red-500 rounded-full"
              data-testid={`candidate-cancel-${c.candidate_id}`}>취소</button>
          )}
          <button onClick={() => copyLink(c.candidate_id)}
            className="px-2 py-1 text-[10px] font-bold border border-black/10 dark:border-white/10 rounded-full"
            data-testid={`candidate-copy-${c.candidate_id}`}>URL 복사</button>
          <Link to={`/live-order/${c.candidate_id}`} target="_blank" rel="noreferrer"
            className="px-2 py-1 text-[10px] font-bold bg-black text-white rounded-full"
            data-testid={`candidate-open-${c.candidate_id}`}>열기</Link>
        </div>
        {c.order_id && (
          <div className="w-full pt-2 mt-1 border-t border-dashed border-orange-500/30">
            <OrderTestConsole orderId={c.order_id} compact />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid="comment-order-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-black inline-flex items-center gap-1">💬 댓글 주문 <span className="text-gray-400 font-medium">(내부 테스트)</span></p>
        {pendingCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-black text-[#FF3B30] bg-[#FF3B30]/10 px-2 py-0.5 rounded-full" data-testid="comment-pending-badge">
            <Bell size={11} /> 미처리 {pendingCount}건
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-500">"4번 블랙 M 2개" 처럼 신규 · "4번 3개로 변경"으로 수정 · "4번 취소"로 취소를 시뮬레이션할 수 있습니다.</p>

      {/* Phase 15-B follow-up · session-only Comment Reply Toggle -----------
         The state lives in this component and dies when the page unmounts,
         so navigating away or reloading resets to the seller default. */}
      <div className={`flex items-center justify-between gap-2 rounded-xl border p-2.5 ${effectiveOn && !toggleDisabled ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5'}`}
           data-testid="comment-notify-toggle-card">
        <div className="min-w-0">
          <p className="text-[11px] font-black inline-flex items-center gap-1">
            <Bell size={11} className="text-[#FF3B30]" />
            {toggleDisabled
              ? <span data-testid="comment-notify-state">주문확정 알림 OFF · 판매자 마스터 OFF</span>
              : effectiveOn
                ? <span data-testid="comment-notify-state">주문확정 알림 ON</span>
                : <span data-testid="comment-notify-state">주문확정 알림 OFF</span>}
            {sendNotify != null && !toggleDisabled && (
              <span className="text-[9px] font-black text-orange-500 bg-orange-500/10 px-1.5 py-0.5 rounded-full" data-testid="comment-notify-session-badge">
                이 라이브만 적용
              </span>
            )}
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">
            실제 알림톡 연동 시 발송 건당 판매자 충전금이 차감됩니다. 현재는 <b>TEST MODE</b> — 실제 발송·차감이 발생하지 않습니다.
            {sendNotify != null && !toggleDisabled && (
              <button onClick={() => setSendNotify(null)}
                      className="ml-1 underline text-[10px] font-bold hover:text-black dark:hover:text-white"
                      data-testid="comment-notify-reset">기본값으로 복귀</button>
            )}
          </p>
        </div>
        <button onClick={() => !toggleDisabled && setSendNotify(!effectiveOn)}
                disabled={toggleDisabled}
                aria-pressed={effectiveOn}
                aria-label="댓글주문 확정 알림 보내기 토글"
                className={`shrink-0 relative w-11 h-6 rounded-full transition-colors ${toggleDisabled ? 'bg-gray-400/40 cursor-not-allowed' : effectiveOn ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-white/20'}`}
                data-testid="comment-notify-toggle">
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${effectiveOn && !toggleDisabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      {/* Simulator */}
      <div className="flex flex-wrap gap-2">
        <input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="작성자"
          className="w-32 px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
          data-testid="comment-buyer" />
        <input value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && parse()}
          placeholder="예: 4번 블랙 M 2개 / 4번 3개로 변경 / 4번 취소"
          className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-sm bg-transparent"
          data-testid="comment-input" />
        <button onClick={parse} disabled={busy}
          className="px-4 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40"
          data-testid="comment-parse-btn">{busy ? '분석 중…' : '댓글 분석'}</button>
      </div>

      {/* Phase 14-T · Platform filter tabs (client-side against Candidate.source) */}
      <div className="-mx-1 px-1 overflow-x-auto scrollbar-none">
        <div className="flex gap-1 flex-nowrap min-w-fit" data-testid="candidate-platform-tabs">
          {PLATFORM_TABS.map((p) => {
            const n = platformCounts[p.key] ?? 0;
            return (
              <button key={p.key} onClick={() => setPlatform(p.key)}
                className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                  platform === p.key ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-black/30 dark:hover:border-white/30'
                }`}
                data-testid={p.testid}>
                {p.label}<span className="ml-1 opacity-70">{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tabs + counts */}
      <div className="flex gap-1 flex-wrap" data-testid="candidate-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1 rounded-full text-[11px] font-bold border transition-colors ${
              tab === t.key ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'
            }`}
            data-testid={`tab-${t.key}`}>
            {t.label}
            <span className="ml-1 opacity-70">{counts[t.key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="구매자명 · 댓글 · 상품명 검색"
          className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
          data-testid="candidate-search" />
      </div>

      {/* Candidate list */}
      <div className="space-y-2" data-testid="comment-candidates">
        {rows.length === 0 && <p className="text-xs text-gray-400 text-center py-4">해당 조건의 후보가 없습니다.</p>}
        {fresh.map(renderRow)}
        {old.length > 0 && (
          <div>
            <button onClick={() => setShowOld((v) => !v)}
              className="w-full py-2 rounded-lg text-[11px] font-bold text-gray-500 border border-dashed border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
              data-testid="candidate-toggle-old">
              {showOld ? '이전 후보 접기' : `이전 후보 ${old.length}건 펼치기 (10분 이전)`}
            </button>
            {showOld && <div className="space-y-2 mt-2" data-testid="candidate-old-list">{old.map(renderRow)}</div>}
          </div>
        )}
      </div>

      <CandidateDetailModal open={!!detailId} candidateId={detailId} onClose={() => setDetailId(null)} />
      <CancelReasonModal open={!!cancelId} onClose={() => setCancelId(null)}
        onConfirm={(reason, note) => doSellerCancel(cancelId, reason, note)} />
    </div>
  );
};

// ---- Main page -----------------------------------------------------------
// STUDIO-NOTICE-UI-CONSOLIDATION-1 · 섹션 숨김 체크박스 (TEST).
const HideBox = ({ label, checked, onChange, testid }) => (
  <div className={`flex items-center gap-2 ${label && checked ? '' : 'mb-1'}`}>
    {label && <span className="inline-flex items-center gap-1.5 text-xs font-black text-gray-900 dark:text-white"><Megaphone size={12} className="text-[#FF3B30]" />{label}</span>}
    <label className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />숨김
    </label>
  </div>
);

const SellerLiveControl = ({ user }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [broadcasts, setBroadcasts] = useState(null);
  const [activeScheduleId, setActiveScheduleId] = useState(params.get('sid') || '');
  const [data, setData] = useState(null);
  const [testViewerOpen, setTestViewerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  // STUDIO-BLUETOOTH-QUICK-REGISTER-1 · Studio 셔터 캡처 → 기존 빠른등록(소개 자동 변경 없음).
  const [inboxCapture, setInboxCapture] = useState(null);
  // LIVE-TEST-STUDIO-CONSOLE-PARITY-1 · TEST 공유 세션 (Studio 와 동일 상품·소개 상태 · 실제 방송 API 호출 없음).
  const isTest = activeScheduleId === TEST_SID;
  useShortToast(isTest);
  const [hideExt, setHideExt] = useState(() => readHidePref('external_notice'));
  const [hideQuick, setHideQuick] = useState(() => readHidePref('quick_add'));
  const [testCtx, setTestCtx] = useState(null);
  const testCtxRef = useRef(null);
  testCtxRef.current = testCtx;
  const testShare = useTestLiveShare({ enabled: isTest, ctx: testCtx, setCtx: setTestCtx, seed: buildTestShared });
  useEffect(() => { if (isTest) setData(toConsoleData(testCtx)); }, [isTest, testCtx]);
  const testDo = (fn, msg) => { setTestCtx((c) => (c ? fn(c) : c)); if (msg) toast.success(`🧪 ${msg}`); };
  // REAL-CONTROL-INTEGRATION-1 · 실제 방송: 판매자별 타이머 문구·외부공지 (Studio 와 같은 저장소).
  const _realSid = !isTest ? (data?.streaming_session_id || null) : null;
  const livePresetsC = useLivePresets(!isTest && !!_realSid);
  const realCtxC = realPresetCtx(livePresetsC.presets, data?.external_notice?.id, data?.timer);
  const realApplyC = makeRealApply({ ctx: realCtxC, save: livePresetsC.save, sessionId: _realSid,
    onSelected: (r) => setData((d) => (d ? { ...d, external_notice: r } : d)) });
  const presetCtxC = isTest ? testCtx : realCtxC;
  const presetApplyC = isTest ? (fn) => testDo(fn) : realApplyC;
  const showPresetsC = isTest || !!livePresetsC.presets;
  const testQuickRegister = async (body) => {
    const r = testOps.quickAdd(testCtxRef.current, body);
    setTestCtx(r.ctx);
    return { product: r.product, slot_number: r.product.slot_number, announced: !!body.announce_now };
  };
  const toggleTest = () => {
    if (isTest) {
      setTestCtx(null);
      setData(null);
      const next = new URLSearchParams(params);
      next.delete('sid');
      setParams(next, { replace: true });
      setActiveScheduleId(broadcasts?.[0]?.schedule_id || '');
    } else setActiveScheduleId(TEST_SID);
  };
  const [shareOpen, setShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · 공통 LIVE action hook (canonical /announce).
  // 아래 announce / applyOverlayPreset / hideOverlay / clearAnnounce /
  // overlay-inventory onClick / overlay-qr-show onClick / overlay-qr-hide
  // onClick 가 전부 이 hook 을 통과하도록 wiring.  동일 contract 를 Console
  // (SellerLiveConsole ProductsTab) 가 공유한다.
  const ops = useLiveOperations(activeScheduleId);
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · 공통 Notice editor hook 으로 치환.
  // Console (SellerLiveConsole) 과 동일 shared hook — state + /uploads + PUT/DELETE
  // /seller/streaming/sessions/{sid}/notice 를 hook 이 담당, Studio 는 UI + side
  // effect (loadDetail) 만 담당.  canonical contract (text + image_url +
  // image_size) 100% 그대로 REUSE · 신규 endpoint 금지.
  const _noticeEditor = useNoticeEditorAutoHydrate(
    data?.streaming_session_id || null,
    data?.live_notice || null,
    { onSaved: () => loadDetail() },
  );
  // STEP OVERLAY-CONTROL-UX-1 · 판매자 공용 Notice 보관함 shared hook.
  const noticeLibrary = useNoticeLibrary();
  // STEP LIVE-CONSOLE-OVERLAY-COLLAPSE-PIN-1 · Overlay 제어 박스 접기/고정 preference.
  // seller 계정 단위 localStorage · Overlay runtime (announce/display/qr/notice/timer) 미영향.
  const overlayPanels = useOverlayPanelsPreference(user?.seller_id || '');
  const noticeDraft = _noticeEditor.draft;
  const setNoticeDraft = _noticeEditor.setDraft;
  const noticeImageUrl = _noticeEditor.imageUrl;
  const setNoticeImageUrl = _noticeEditor.setImageUrl;
  const noticeImageSize = _noticeEditor.imageSize;
  const setNoticeImageSize = _noticeEditor.setImageSize;
  const noticeBusy = _noticeEditor.busy;
  const noticeImageUploading = _noticeEditor.imageUploading;
  // uploadNoticeImage / saveLiveNotice / clearLiveNotice 는 hook 이 제공 — 아래
  // Notice 섹션은 이름 그대로 사용 (downstream JSX 변경 없음).
  const uploadNoticeImage = (file) => _noticeEditor.uploadImage(file);
  const saveLiveNotice = () => _noticeEditor.save();
  const clearLiveNotice = () => _noticeEditor.clear();
  // Load the seller display name once so it can be surfaced in share previews.
  const [sellerName, setSellerName] = useState('');
  useEffect(() => {
    if (!user?.seller_id) return;
    api.get(`/sellers/${user.seller_id}`).then((r) => setSellerName(r.data?.name || '')).catch(() => {});
  }, [user?.seller_id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/seller/broadcasts/today');
        const list = res.data || [];
        setBroadcasts(list);
        // STUDIO-CAPTURE-INBOX-VISIBILITY-FIX-1 · 오늘 외 예정 방송도 선택 가능 (본인 방송 목록 REUSE).
        const ups = await loadUpcoming(list);
        setUpcoming(ups);
        const sid = params.get('sid');
        const pick = sid === TEST_SID ? TEST_SID
          : sid && (list.some((b) => b.schedule_id === sid) || ups.some((b) => b.schedule_id === sid))
          ? sid
          : (list[0]?.schedule_id || '');
        setActiveScheduleId(pick);
      } catch (e) { toast.error('오늘 방송을 불러오지 못했습니다.'); setBroadcasts([]); }
    })();
    // eslint-disable-next-line
  }, []);

  const loadDetail = useCallback(async () => {
    if (!activeScheduleId) { setData(null); return; }
    if (activeScheduleId === TEST_SID) return;
    try {
      const res = await api.get(`/seller/broadcasts/${activeScheduleId}/live-products`);
      setData(res.data);
    } catch (e) { toast.error('상품 목록을 불러오지 못했습니다.'); }
  }, [activeScheduleId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // STEP OVERLAY-PC-MOBILE-CONTROLS-COMPLETE · live_notice / active_product
  // 는 soft-realtime 동기화 (사용자가 모바일에서 바꿔도 PC 가 갱신).
  // 전역 polling 신설 없이 10초 간격 단일 interval. loadDetail 자체가 cheap.
  useEffect(() => {
    if (!activeScheduleId) return;
    const t = setInterval(() => { loadDetail(); }, 10000);
    return () => clearInterval(t);
  }, [activeScheduleId, loadDetail]);

  // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · hydration 은 useNoticeEditorAutoHydrate 가 담당.
  // (기존 수동 useEffect 삭제 · 중복 로직 0 · live_notice snapshot 변경 시 자동 재hydrate.)

  // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · uploadNoticeImage / saveLiveNotice /
  // clearLiveNotice 는 useNoticeEditor hook 이 담당 (위 _noticeEditor alias).

  useEffect(() => {
    if (!activeScheduleId) return;
    if (params.get('sid') !== activeScheduleId) {
      const next = new URLSearchParams(params);
      next.set('sid', activeScheduleId);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [activeScheduleId]);

  // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · canonical ops.announce REUSE.
  const announce = async (productId, preset) => {
    if (isTest) { testDo((c) => testOps.announce(c, productId), 'TEST 소개 전환'); return; }
    setBusy(true);
    try {
      await ops.announce(productId, preset);
      toast.success(preset ? `소개중 상품 · ${preset.toUpperCase()} 적용` : '현재 소개중 상품으로 설정되었습니다.');
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '설정 실패'); }
    finally { setBusy(false); }
  };
  // STEP OVERLAY-2C-PRODUCT-CONTROL · Overlay preset control.
  // 현재 active_product_id 를 유지한 채 preset 만 교체 (Studio button /
  // Seller Console / Future MOA Assistant 가 동일하게 호출).
  const applyOverlayPreset = async (preset) => {
    if (!data?.active_product_id) { toast.error('먼저 소개중 상품을 선택해주세요.'); return; }
    // PC-CONSOLE-OVERLAY-PRESET-CONSUMER-VIEW-1 · TEST 는 Studio 와 같은 active_product_display.preset 공유 (실제 API 미호출).
    if (isTest) {
      testDo((c) => ({ ...c, active_product_display: { ...(c.active_product_display || {}), preset, mode: 'visible' } }), `TEST 표시 형태 · ${preset}`);
      return;
    }
    await announce(data.active_product_id, preset);
  };
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · canonical ops.hideOverlay / clearAnnounce REUSE.
  const hideOverlay = async () => {
    if (!data?.active_product_id) return;
    if (isTest) { testDo(testOps.hideOverlay, 'TEST Overlay 숨김'); return; }
    setBusy(true);
    try {
      await ops.hideOverlay(data.active_product_id);
      toast.success('Overlay 숨김');
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '숨김 실패'); }
    finally { setBusy(false); }
  };
  const clearAnnounce = async () => {
    if (isTest) { testDo(testOps.clear, 'TEST 소개 종료'); return; }
    setBusy(true);
    try {
      await ops.clearAnnounce();
      toast.success('현재 소개중 상품이 해제되었습니다.');
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '해제 실패'); }
    finally { setBusy(false); }
  };
  const endBroadcast = async () => {
    if (isTest) { toast.info('TEST 모드 · 실제 방송 종료 없음'); return; }
    if (!window.confirm('방송을 종료하시겠습니까? 소개중 상품이 해제되고 상태가 "종료"로 변경됩니다. 상품 목록은 그대로 남습니다.')) return;
    setBusy(true);
    try {
      await api.post(`/seller/broadcasts/${activeScheduleId}/end`);
      toast.success('방송이 종료되었습니다.');
      loadDetail();
      // Phase 5.7 · 종료 직후 리포트 모달 자동 오픈
      setReportOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || '종료 실패'); }
    finally { setBusy(false); }
  };
  const markSoldOut = async (pid) => {
    if (!window.confirm('현재 상품을 품절 처리하시겠습니까? 소개중 상품이면 다음 판매 가능한 상품으로 자동 이동합니다.')) return;
    if (isTest) { testDo((c) => testOps.soldout(c, pid), 'TEST 품절 처리'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/seller/broadcasts/${activeScheduleId}/live-products/${pid}/soldout`);
      if (r.data?.advanced_to) toast.success('품절 처리 완료 · 다음 상품으로 이동');
      else toast.success('품절 처리 완료');
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '품절 처리 실패'); }
    finally { setBusy(false); }
  };
  const removeItem = async (pid) => {
    if (!window.confirm('이 상품을 오늘 방송에서 제거하시겠습니까?')) return;
    if (isTest) { testDo((c) => testOps.remove(c, pid), 'TEST 목록에서 제거'); return; }
    setBusy(true);
    try {
      await api.delete(`/seller/broadcasts/${activeScheduleId}/live-products/${pid}`);
      toast.success('제거되었습니다.');
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '제거 실패'); }
    finally { setBusy(false); }
  };
  const saveSlot = async (pid, newSlot) => {
    if (!Number.isFinite(newSlot) || newSlot < 1) { toast.error('올바른 번호를 입력하세요.'); return; }
    if (isTest) { testDo((c) => testOps.setSlot(c, pid, newSlot)); setEditSlot(null); return; }
    setBusy(true);
    try {
      await api.patch(`/seller/broadcasts/${activeScheduleId}/live-products/${pid}`, { slot_number: newSlot });
      setEditSlot(null);
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '번호 변경 실패'); }
    finally { setBusy(false); }
  };
  // ▲▼ reorder using the new bulk-reorder endpoint (server persists sort_order).
  const move = async (pid, dir) => {
    if (!data?.items) return;
    const ordered = data.items.slice().sort((a, b) => a.sort_order - b.sort_order).map((it) => it.product_id);
    const idx = ordered.indexOf(pid);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= ordered.length) return;
    [ordered[idx], ordered[target]] = [ordered[target], ordered[idx]];
    if (isTest) { testDo((c) => testOps.reorder(c, ordered)); return; }
    setBusy(true);
    try {
      await api.post(`/seller/broadcasts/${activeScheduleId}/live-products/reorder`, { product_ids: ordered });
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '순서 변경 실패'); }
    finally { setBusy(false); }
  };

  // ---- Drag & Drop reorder · HTML5 native · reuses the same reorder API. ----
  const [dragPid, setDragPid] = useState(null);
  // FINAL-VISUAL-MATCH-1 (지시 §6 §8) · 방송상품 local 검색 state — frontend only.
  const [productSearch, setProductSearch] = useState('');
  const [dragOverPid, setDragOverPid] = useState(null);

  // Phase 6.1 HOTFIX · 프로필에 "방송상품" 섹션 노출 ON/OFF · profile_home_config.show_live_products 재사용
  const [profileShowLive, setProfileShowLive] = useState(null); // null = 로딩 중
  useEffect(() => {
    api.get('/seller/profile-home-config')
      .then((r) => setProfileShowLive(r.data?.show_live_products !== false))
      .catch(() => setProfileShowLive(true));
  }, []);
  const toggleProfileShowLive = async () => {
    if (profileShowLive === null) return;
    if (isTest) { toast.info('TEST 모드 · 프로필 설정 변경 없음'); return; }
    const next = !profileShowLive;
    setProfileShowLive(next);
    try {
      await api.patch('/seller/profile-home-config', { show_live_products: next });
      toast.success('방송상품 노출 설정이 변경되었습니다.');
    } catch (e) {
      setProfileShowLive(!next);
      toast.error(e?.response?.data?.detail || '저장 실패');
    }
  };
  const onDragStart = (pid) => (e) => { setDragPid(pid); try { e.dataTransfer.effectAllowed = 'move'; } catch {} };
  const onDragOver  = (pid) => (e) => { e.preventDefault(); if (pid !== dragOverPid) setDragOverPid(pid); };
  const onDragEnd   = () => { setDragPid(null); setDragOverPid(null); };
  const onDrop      = (targetPid) => async (e) => {
    e.preventDefault();
    const src = dragPid;
    onDragEnd();
    if (!src || src === targetPid || !data?.items) return;
    const ordered = data.items.slice().sort((a, b) => a.sort_order - b.sort_order).map((it) => it.product_id);
    const from = ordered.indexOf(src);
    const to   = ordered.indexOf(targetPid);
    if (from < 0 || to < 0) return;
    ordered.splice(from, 1);
    ordered.splice(to, 0, src);
    if (isTest) { testDo((c) => testOps.reorder(c, ordered)); return; }
    setBusy(true);
    try {
      await api.post(`/seller/broadcasts/${activeScheduleId}/live-products/reorder`, { product_ids: ordered });
      loadDetail();
    } catch (e2) { toast.error(e2?.response?.data?.detail || '순서 변경 실패'); }
    finally { setBusy(false); }
  };

  // ---- Visibility mode (all | introduced_only) — 기본 all · 기존 방송 무손상 ----
  // 서버 응답 필드가 없을 수도 있으므로 항상 'all' 폴백. useState 초기값도 'all'.
  const visMode = data?.product_visibility_mode || 'all';
  const changeVisMode = async (mode) => {
    if (!activeScheduleId || mode === visMode) return;
    if (isTest) { testDo((c) => testOps.visibility(c, mode), mode === 'all' ? 'TEST 전체 공개' : 'TEST 소개한 상품만'); return; }
    setBusy(true);
    try {
      await api.patch(`/seller/broadcasts/${activeScheduleId}/visibility-mode`, { mode });
      toast.success(mode === 'all' ? '전체 상품 바로 공개 모드' : '소개한 상품만 공개 모드');
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '변경 실패'); }
    finally { setBusy(false); }
  };

  // ---- 개별 다시 가리기 · introduced 상태만 해제 (주문·재고·순서 유지) --------
  const unintroduce = async (pid) => {
    if (!activeScheduleId || !pid) return;
    if (isTest) { testDo((c) => testOps.unintroduce(c, pid), 'TEST 다시 가리기'); return; }
    setBusy(true);
    try {
      await api.post(`/seller/broadcasts/${activeScheduleId}/live-products/${pid}/unintroduce`);
      toast.success('다시 가리기 완료 · 곧 진행 상태로 복귀했습니다.');
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '다시 가리기 실패'); }
    finally { setBusy(false); }
  };

  // ---- 소개상태 전체 초기화 · 방송 내 모든 상품 introduced 해제 ---------------
  const resetIntroduced = async () => {
    if (!activeScheduleId) return;
    if (!window.confirm('현재 방송의 상품 소개 상태를 모두 초기화하시겠습니까?\n상품 순서와 주문 데이터는 유지됩니다.')) return;
    if (isTest) { testDo(testOps.resetIntroduced, 'TEST 소개상태 초기화'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/seller/broadcasts/${activeScheduleId}/live-products/reset-introduced`);
      toast.success(`소개상태 초기화 완료 (${r.data?.reset_count ?? 0}건)`);
      loadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || '초기화 실패'); }
    finally { setBusy(false); }
  };

  const items = useMemo(
    () => (data?.items || []).slice().sort((a, b) => a.sort_order - b.sort_order),
    [data]
  );
  const activeIndex = items.findIndex((it) => it.product_id === data?.active_product_id);
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;
  const currentIds = useMemo(() => new Set(items.map((it) => it.product_id)), [items]);

  // Live remote — walk to next/prev IN-STOCK item, skipping sold-out.
  const gotoRelative = async (delta) => {
    if (items.length === 0) return;
    if (activeIndex < 0) { announce(items[0].product_id); return; }
    let i = activeIndex + delta;
    while (i >= 0 && i < items.length) {
      const cand = items[i];
      if ((cand.sale_status || '판매 중') !== '품절') { announce(cand.product_id); return; }
      i += delta;
    }
    toast.error(delta > 0 ? '다음 판매 가능한 상품이 없습니다.' : '이전 판매 가능한 상품이 없습니다.');
  };

  if (broadcasts === null) {
    return <div className="max-w-3xl mx-auto p-10 text-sm text-gray-400">불러오는 중…</div>;
  }

  const canPrev = items.length > 0 && items.slice(0, Math.max(activeIndex, 0)).some((it) => (it.sale_status || '판매 중') !== '품절');
  const canNext = items.length > 0 && items.slice(activeIndex + 1).some((it) => (it.sale_status || '판매 중') !== '품절');

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 space-y-3" data-testid="live-control-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black dark:hover:text-white" data-testid="live-control-back">
        <ArrowLeft size={14} /> 뒤로
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-black inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <span className="inline-block w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" /> LIVE Console
        </h1>
        {/* FINAL-VISUAL-MATCH-1 (지시 §1 §4): 상단 방송중/완료 pill badge · 기존 broadcasts status REUSE. */}
        {(() => {
          const _bs = broadcasts || [];
          const _liveNow = _bs.some(b => b.status === '라이브' || b.status === 'live');
          const _endedOnly = _bs.length > 0 && _bs.every(b => b.status === '종료' || b.status === 'ended');
          if (_liveNow) return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-black"
                  data-testid="live-console-status-live">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> 방송중
            </span>
          );
          if (_endedOnly) return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-300 dark:bg-white/20 text-gray-700 dark:text-gray-200 text-[11px] font-black"
                  data-testid="live-console-status-ended">종료</span>
          );
          if (_bs.length > 0) return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[11px] font-black"
                  data-testid="live-console-status-scheduled">예정</span>
          );
          return null;
        })()}
        <button type="button" onClick={toggleTest}
          className={`px-3 py-1 rounded-full text-[11px] font-black border ${isTest ? 'bg-amber-400 text-black border-amber-500' : 'border-amber-500/60 text-amber-700 dark:text-amber-300'}`}
          data-testid="live-control-test-mode-toggle">{isTest ? 'TEST 나가기' : 'TEST 모드'}</button>
        {isTest && (
          <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300" data-testid="live-control-test-banner">
            🧪 TEST 공유 세션{testShare.account && <b data-testid="live-control-test-account"> [{testShare.account.name} · {testShare.account.id.slice(-6)}]</b>} · 실제 방송·주문·상품 변경 없음 ·{' '}
            <span data-testid="live-control-test-share-status">{testShare.status === 'error' ? '동기화 오류' : testShare.status === 'synced' ? 'Studio와 동기화 중' : '연결 중'}</span>
          </span>
        )}
      </div>

      {/* STEP LIVE-CONSOLE-REALTIME-SUMMARY-1 · Phase55LiveHeader 대형 현황을
          compact 1-2 line bar 로 교체 (지시 §1 §2).  기존 Phase55 컴포넌트는
          재사용 지점이 없으므로 Console 에서만 교체 — business logic / API 0 변경.
          기능/데이터 삭제 아님 — 큰 영역 → 작은 영역 재배치. */}
      {activeScheduleId && !isTest && (
        <CompactLiveSummary scheduleId={activeScheduleId} />
      )}

      {/* STEP LIVE-CONSOLE-REALTIME-SUMMARY-1 · 오늘 방송 Compact 한 줄 bar
          (지시 §7).  activeScheduleId / 선택 로직은 그대로 REUSE. */}
      <div className="rounded-xl border border-black/10 dark:border-white/10 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
           data-testid="today-broadcasts">
        {broadcasts.length === 0 ? (
          <>
            <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-gray-500">
              <Radio size={11} /> 오늘 방송
            </span>
            <span className="text-gray-500">오늘 예정된 방송이 없습니다.</span>
            <Link to="/seller/dashboard?section=broadcasts"
                  target="_blank" rel="noopener noreferrer"
                  className="ml-auto text-[#FF3B30] font-bold inline-flex items-center gap-1"
                  data-testid="today-broadcasts-register">
              방송 등록 <ExternalLink size={10} />
            </Link>
          </>
        ) : broadcasts.length === 1 ? (
          (() => {
            const b = broadcasts[0];
            const isLive = b.status === '라이브' || b.status === 'live';
            const label  = isLive ? 'LIVE'
                          : (b.status === '종료' || b.status === 'ended' ? '종료' : '예정');
            const dot    = isLive ? 'bg-[#FF3B30] animate-pulse'
                          : (label === '종료' ? 'bg-gray-400' : 'bg-amber-500');
            return (
              <>
                <span className="inline-flex items-center gap-1 font-black" data-testid="today-broadcast-status">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
                  {label}
                </span>
                <span className="inline-block px-1.5 py-0.5 rounded-full bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] font-bold">
                  자동선택
                </span>
                <span className="font-bold truncate max-w-[18rem]">{b.title}</span>
                <span className="text-gray-500">
                  · {format(new Date(b.scheduled_at), 'M월 d일 HH:mm', { locale: ko })}
                </span>
              </>
            );
          })()
        ) : (
          <>
            <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-gray-500 shrink-0">
              <Radio size={11} /> 오늘 방송
            </span>
            <div className="flex flex-wrap gap-1" data-testid="today-broadcasts-picker">
              {broadcasts.map((b) => (
                <button key={b.schedule_id}
                  onClick={() => setActiveScheduleId(b.schedule_id)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${
                    activeScheduleId === b.schedule_id
                      ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                      : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'
                  }`}
                  data-testid={`pick-broadcast-${b.schedule_id}`}>
                  {format(new Date(b.scheduled_at), 'HH:mm', { locale: ko })} · {b.title}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <UpcomingPicker items={upcoming} activeId={activeScheduleId} onPick={setActiveScheduleId} />

      {/* STEP LIVE-CONSOLE-ROLE-SPLIT-UX-1 · 2-column split (LEFT: Product/Ops ·
          RIGHT: Monitor/Response).  기존 single-column 모든 anchor 를 LEFT/RIGHT
          분배로 그대로 보존 (지시 §5 §11).  신규 backend endpoint / collection 0. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_440px] gap-4 items-start"
           data-testid="live-control-split">

      {/* LEFT · Product 운영 영역 (상품 · 소개중 · 라이브 상품 목록 + 방송 부가기능). */}
      <div className="min-w-0 flex flex-col gap-3" data-testid="live-control-left">

      {/* STEP MINI-CONSOLE-STUDIO-UX-1 · 외부 LIVE 미니주문 운영 섹션 **메인 승격**.
          · 지시 §3 · 상품 리스트 **위** 에 접기형 섹션으로 노출.
          · 모바일 (lg 미만): 기본 접힘 (defaultCollapsed · 사용자 탭하여 전개).
          · PC: 기본 전개 — 판매 운영의 중심 기능으로 활용 가능.
          · 기존 LIVE 조작 / Overlay / 상품 리스트 영향 0 (별도 상단 section 삽입만).
          · lg:hidden 로 PC/모바일 defaultCollapsed 분기 (중복 render 금지). */}
      <div className="lg:hidden" data-testid="live-control-ext-mini-mobile-slot">
        <ExternalMiniSection defaultCollapsed variant="full" />
      </div>
      <div className="hidden lg:block" data-testid="live-control-ext-mini-pc-slot">
        <ExternalMiniSection defaultCollapsed={false} variant="full" />
      </div>

      {/* STEP LIVE-CONSOLE-COMPACT-LAYOUT-1 · 작업영역 그룹.
          `현재 소개중` + `오늘 라이브 상품` + overlay controls + 상품 리스트 를
          하나의 연속 상품 작업영역으로 묶어 세로 공간 낭비 제거 (지시 §3).
          기능/route/API 0 변경 — flex-col + 작은 gap 만 도입. */}
      <div className="flex flex-col gap-2" data-testid="live-control-workarea">

      {/* 2. 현재 소개중 상품 (sticky) + LIVE REMOTE */}
      <div className="sticky top-2 z-10 rounded-2xl border border-[#FF3B30]/60 bg-white dark:bg-[#141414] shadow-sm p-3" data-testid="current-active">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs font-black inline-flex items-center gap-1.5 text-[#FF3B30]">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black"
                  data-testid="section-badge-1">1</span>
            <span className="inline-block w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" /> 현재 소개 상품
          </p>
          <button onClick={() => setShareOpen(true)} disabled={!activeScheduleId}
            className="px-3 py-1 rounded-full text-[11px] font-black bg-black text-white hover:bg-[#FF3B30] inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="active-share-btn"><Share2 size={11} /> 공유</button>
        </div>
        {activeItem ? (
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-8 rounded-full bg-[#FF3B30] text-white text-sm font-black flex items-center justify-center shrink-0" data-testid="current-slot">{activeItem.slot_number}</span>
            <SafeImg src={activeItem.image} kind="product" className="w-14 h-14 rounded object-cover" alt="" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black truncate" data-testid="current-name">{activeItem.name}</p>
              <p className="text-sm font-bold text-[#FF3B30]" data-testid="current-price">{won(activeItem.sale_price)}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-3" data-testid="current-empty">아직 소개중 상품이 없습니다. 아래 목록에서 <b>소개하기</b>를 눌러 시작하세요.</p>
        )}
        {/* Remote controls — always rendered so sellers can access them
             even before the first product is pinned. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="live-remote">
          <button disabled={busy || !canPrev} onClick={() => gotoRelative(-1)}
            className="px-3 py-2 rounded-full text-xs font-bold border border-black/10 dark:border-white/10 inline-flex items-center justify-center gap-1 disabled:opacity-40"
            data-testid="remote-prev"><ChevronLeft size={14} /> 이전상품</button>
          <button disabled={busy || !canNext} onClick={() => gotoRelative(1)}
            className="px-3 py-2 rounded-full text-xs font-bold bg-black text-white inline-flex items-center justify-center gap-1 disabled:opacity-40"
            data-testid="remote-next">다음상품 <ChevronRight size={14} /></button>
          <button disabled={busy || !activeItem} onClick={() => markSoldOut(activeItem.product_id)}
            className="px-3 py-2 rounded-full text-xs font-bold border border-orange-500/40 text-orange-600 inline-flex items-center justify-center gap-1 disabled:opacity-40"
            data-testid="remote-soldout"><XCircle size={14} /> 품절</button>
          <button disabled={busy || !activeItem} onClick={clearAnnounce}
            className="px-3 py-2 rounded-full text-xs font-bold border border-gray-400/40 text-gray-600 inline-flex items-center justify-center gap-1 disabled:opacity-40"
            data-testid="remote-clear"><StopCircle size={14} /> 소개종료</button>
        </div>
        {/* 현재 소개상품 다시 가리기 · introduced_only 모드에서만 의미 있음 */}
        {activeItem && visMode === 'introduced_only' && (
          <div className="mt-2">
            <button onClick={() => unintroduce(activeItem.product_id)} disabled={busy}
              className="w-full px-3 py-2 rounded-full text-xs font-bold border border-black/20 dark:border-white/20 text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 inline-flex items-center justify-center gap-1"
              title="현재 소개중 상품을 다시 가리기 (곧 진행 복귀)"
              data-testid="remote-unintroduce"><XCircle size={12} /> 현재 상품 다시 가리기</button>
          </div>
        )}
        {activeItem && !canNext && !canPrev && (
          <p className="text-[10px] text-gray-400 mt-2">판매 가능한 다음/이전 상품이 없습니다.</p>
        )}
      </div>

      {/* 3. 오늘 라이브 상품 목록 */}
      {activeScheduleId && (
        <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 flex flex-col" data-testid="live-products">
          <div className="order-0 mb-2">
            <CaptureInbox scheduleId={activeScheduleId}
                          onRegister={(file, capture) => { setInboxCapture({ file, capture }); setQuickOpen(true); }} />
          </div>
          <div className="order-1 flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="text-xs font-black inline-flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black"
                    data-testid="section-badge-2">2</span>
              방송 상품 목록 <span className="text-gray-500 font-medium">({items.length}건)</span>
            </p>
            <div className="flex gap-1.5 flex-wrap items-center">
              {/* FINAL-VISUAL-MATCH-1 (지시 §8) · 방송상품 local 검색 — frontend only.
                  신규 backend endpoint 금지 · 로드된 items 를 상품명/상품번호/SKU 로 필터. */}
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="상품명, 상품번호 검색..."
                className="px-2.5 py-1 rounded-full text-[11px] bg-black/[0.03] dark:bg-white/[0.05] border border-black/10 dark:border-white/10 focus:outline-none focus:border-[#FF3B30] w-40 sm:w-48"
                data-testid="live-products-search-input"
              />
              <button onClick={() => setQuickOpen(true)}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#FF3B30] text-white inline-flex items-center gap-1"
                data-testid="live-quick-btn"><Zap size={11} /> 빠른 등록</button>
              <button onClick={() => (isTest ? toast.info('TEST 모드에서는 운영 상품을 불러오지 않아요 · 빠른 등록을 사용하세요') : setImportOpen(true))}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 inline-flex items-center gap-1"
                data-testid="live-import-btn"><Download size={11} /> 지난 방송 상품</button>
              <button onClick={() => (isTest ? toast.info('TEST 모드에서는 운영 상품을 불러오지 않아요 · 빠른 등록을 사용하세요') : setAddOpen(true))}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 inline-flex items-center gap-1"
                data-testid="live-add-btn"><Plus size={11} /> 전체 상품</button>
              <button onClick={endBroadcast}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-red-500/40 text-red-600 inline-flex items-center gap-1"
                data-testid="live-end-btn"><StopCircle size={11} /> 방송 종료</button>
              <button onClick={() => setReportOpen(true)} disabled={!activeScheduleId}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="live-report-btn"><History size={11} /> 결과 리포트</button>
            </div>
          </div>

          {/* 공개 방식 토글 (기본 all · 기존 동작 보존) */}
          <div className="order-2 mb-3 flex items-center gap-2 flex-wrap p-3 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]" data-testid="visibility-mode-toggle">
            <p className="text-xs font-black tracking-widest uppercase text-gray-500">라이브 상품 공개 방식</p>
            <div className="inline-flex rounded-full border border-black/10 dark:border-white/10 overflow-hidden">
              <button onClick={() => changeVisMode('all')} disabled={busy}
                className={`px-3 py-1.5 text-xs font-bold ${visMode === 'all' ? 'bg-[#FF3B30] text-white' : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'}`}
                data-testid="vis-mode-all">전체 상품 바로 공개/구매</button>
              <button onClick={() => changeVisMode('introduced_only')} disabled={busy}
                className={`px-3 py-1.5 text-xs font-bold ${visMode === 'introduced_only' ? 'bg-[#FF3B30] text-white' : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'}`}
                data-testid="vis-mode-introduced">소개한 상품만 공개/구매</button>
            </div>

            {/* Phase 6.1 HOTFIX · 프로필에 방송상품 노출 ON/OFF (LIVE 콘솔로 이동) */}
            <div className="inline-flex items-center gap-2 pl-2 ml-1 border-l border-black/10 dark:border-white/10" data-testid="profile-show-live-row">
              <span className="text-[11px] font-bold text-gray-600 dark:text-gray-300">프로필에 방송상품 노출</span>
              <label className="relative inline-block w-10 h-6">
                <input type="checkbox" className="peer opacity-0 w-0 h-0"
                  checked={profileShowLive === true}
                  disabled={profileShowLive === null}
                  onChange={toggleProfileShowLive}
                  data-testid="profile-show-live-switch"/>
                <span className="absolute inset-0 rounded-full bg-black/10 peer-checked:bg-[#FF3B30] transition-colors cursor-pointer"/>
                <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow peer-checked:translate-x-4 transition-transform pointer-events-none"/>
              </label>
              <span className="text-[10px] text-gray-500">
                {profileShowLive === true ? 'ON · 프로필 표시' : profileShowLive === false ? 'OFF · 프로필 숨김' : '…'}
              </span>
            </div>

            <button onClick={resetIntroduced} disabled={busy || items.length === 0}
              className="px-3 py-1.5 rounded-full text-xs font-bold border border-black/10 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 inline-flex items-center gap-1"
              title="현재 방송의 모든 상품 소개상태를 초기화합니다. 주문·순서는 유지됩니다."
              data-testid="reset-introduced-btn">
              <History size={12} /> 소개상태 초기화
            </button>
            {visMode === 'introduced_only' && (
              <span className="text-[10px] text-gray-500 ml-1">소개 전 상품은 소비자 화면에서 흐리게 표시됩니다.</span>
            )}
          </div>

          {/* STEP LIVE-CONSOLE-FINAL-UI-POLISH-1 · 7 Overlay panel + Notice 보관함 을
              CSS flex `order` 로 상품 리스트 아래로 재배치 (지시 §6 · DOM 순서 유지).
              DOM 순서는 유지되어 overlay-sections anchor 테스트 LOCK · 시각적으로만
              `4 방송 부가 기능` 섹션처럼 상품 리스트 하단에 노출. */}
          {/* STEP LIVE-CONSOLE-SECTION-CARD-FINAL-1 (지시 §2 §3 §6) · ④ 방송 부가 기능
              을 하나의 명확한 SECTION CARD 로 분리 — live-products 하단에서 full-width
              divider + 밝은 회색 bg 로 시각적 경계를 강화.  grid 2-col 안에서
              [위치|QR] [표시형태|사이즈표] [재고|타이머] [빠른등록|NOTICE] 좌우 균형. */}
          <div className="order-4 -mx-3 mt-4 px-3 pt-3 pb-3 border-t-[3px] border-gray-100 dark:border-white/5 bg-gray-50/60 dark:bg-white/[0.025] rounded-b-2xl flex flex-col gap-2"
               data-testid="aux-features-header">
            <div className="inline-flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black"
                    data-testid="section-badge-4">4</span>
              <span className="text-xs font-black">방송 부가 기능</span>
              <span className="text-[10px] text-gray-500 font-medium">(Overlay · QR · NOTICE · 타이머 · 빠른등록)</span>
            </div>
          {/* STEP OVERLAY-PC-MOBILE-CONTROLS-COMPLETE · Overlay / Inventory /
              QR 는 active_product 가 없어도 항상 노출 · 버튼만 disabled.
              Notice/Timer 는 아예 상품과 무관하게 항상 사용 가능. */}
          {(() => { return null; })()}
          {/* STEP OVERLAY-CONTROL-UX-1 · Overlay 제어 UI 순서 통일 (지시 §B).
              1. 위치  2. QR  3. 표시 형태/방송 중 동작  4. 사이즈표
              5. 재고 표시  6. NOTICE  7. 타이머
              DOM 순서는 유지하고 CSS flex `order` 로만 visual 순서 재정의 (리팩터
              리스크 최소화). 각 섹션에 style={{ order: N }} 를 attach 한다. */}
          <div className="order-5 grid grid-cols-1 lg:grid-cols-2 gap-2"
               data-testid="live-control-overlay-sections">
          {/* STEP OVERLAY-2C-PRODUCT-CONTROL · Overlay Preset Row (compact)
              동일 Action 을 Studio / Seller Console / Future Voice 가 공유.
              `applyOverlayPreset(preset)` → POST /announce { product_id, preset } */}
          <div className="mb-3 p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
               data-testid="overlay-preset-row"
               data-overlay-order="3"
               style={{ order: 3 }}
               data-disabled={!data?.active_product_id ? 'true' : 'false'}>
            <CollapsibleControlSection
              panelKey="preset"
              title="표시 형태 · 방송 중 동작"
              collapsed={overlayPanels.getPanel('preset').collapsed}
              pinned={overlayPanels.getPanel('preset').pinned}
              onToggle={() => overlayPanels.toggleCollapsed('preset')}
              onPin={(v) => overlayPanels.setPinned('preset', v)}
              testIdPrefix="overlay-preset">
            {/* STEP OVERLAY-REAL-UI-POLISH · 짧은 설명 (지시 §2 · mobile tooltip 의존 ❌) */}
            <p className="text-[10px] text-gray-500 leading-snug mb-1.5"
               data-testid="overlay-preset-helper">
              예: 풀 · 이미지 · 옵션 {!data?.active_product_id && (
                <span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>
              )}
            </p>
            <div className="flex items-center gap-1 flex-wrap"
                 data-testid="overlay-mode-row">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">표시 형태</span>
            {/* STEP OVERLAY-PRODUCT-UI-FINAL · 판매자-facing 간소화 (지시 §4/§12).
                백엔드 preset 8종 LOCK · UI 는 '표시 형태' 3개 + '방송 중 동작' 분리. */}
            {[
              ['full', '풀'],
              ['default', '이미지'],
              ['options', '옵션'],
            ].map(([pk, label]) => (
              <button key={pk} onClick={() => applyOverlayPreset(pk)}
                disabled={busy || !data?.active_product_id}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                  data?.active_product_display?.mode !== 'hidden' && (data?.active_product_display?.preset || 'default') === pk && data?.active_product_id
                    ? 'bg-black text-white border-black dark:bg-white dark:text-black' : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'}`}
                data-active={data?.active_product_display?.mode !== 'hidden' && (data?.active_product_display?.preset || 'default') === pk ? '1' : '0'}
                data-testid={`overlay-preset-${pk}`}>{label}</button>
            ))}
            <span className="mx-1 text-gray-300">·</span>
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">방송 중 동작</span>
            {[
              ['emphasized', '강조'],
              ['mini', '미니'],
            ].map(([pk, label]) => (
              <button key={pk} onClick={() => applyOverlayPreset(pk)}
                disabled={busy || !data?.active_product_id}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                  data?.active_product_display?.mode !== 'hidden' && data?.active_product_display?.preset === pk
                    ? 'bg-black text-white border-black dark:bg-white dark:text-black' : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'}`}
                data-active={data?.active_product_display?.mode !== 'hidden' && data?.active_product_display?.preset === pk ? '1' : '0'}
                data-testid={`overlay-preset-${pk}`}>{label}</button>
            ))}
            <button onClick={hideOverlay} disabled={busy || !data?.active_product_id}
              className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40 disabled:cursor-not-allowed ml-1"
              data-testid="overlay-preset-hidden">숨기기</button>
            </div>
            </CollapsibleControlSection>
          </div>

          {/* STEP OVERLAY-FINAL-CONTROL-UX · Overlay Position Row (compact).
              Console 과 동일 contract (ops.setPosition · /announce body
              display:{position}) 로 Studio 에서도 위치 pills (상단/중앙/하단)
              조작 가능.  신규 endpoint·collection 금지. */}
          <div className="mb-3 p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
               data-testid="overlay-position-row"
               data-overlay-order="1"
               style={{ order: 1 }}
               data-disabled={!data?.active_product_id ? 'true' : 'false'}
               data-position={data?.active_product_display?.position || 'bottom'}>
            <CollapsibleControlSection
              panelKey="position"
              title="위치"
              collapsed={overlayPanels.getPanel('position').collapsed}
              pinned={overlayPanels.getPanel('position').pinned}
              onToggle={() => overlayPanels.toggleCollapsed('position')}
              onPin={(v) => overlayPanels.setPinned('position', v)}
              testIdPrefix="overlay-position">
            <p className="text-[10px] text-gray-500 leading-snug mb-1.5"
               data-testid="overlay-position-helper">
              예: 상단 · 중앙 · 하단 {!data?.active_product_id && (
                <span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>
              )}
            </p>
            <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">위치</span>
            {[
              ['top', '상단'],
              ['middle', '중앙'],
              ['bottom', '하단'],
            ].map(([pk, label]) => {
              const cur = data?.active_product_display?.position || 'bottom';
              const active = cur === pk;
              const currentPreset = data?.active_product_display?.preset || 'default';
              return (
                <button key={pk} disabled={busy || !data?.active_product_id}
                  onClick={async () => {
                    // STEP OVERLAY-FINAL-CONTROL-UX · ops.setPosition REUSE.
                    if (isTest) { testDo((c) => ({ ...c, active_product_display: { ...(c.active_product_display || {}), position: pk } }), `TEST 위치 · ${label}`); return; }
                    setBusy(true);
                    try {
                      await ops.setPosition(data.active_product_id, currentPreset, pk);
                      toast.success(`위치 · ${label}`);
                      loadDetail();
                    } catch (e) { toast.error(e?.response?.data?.detail || '위치 변경 실패'); }
                    finally { setBusy(false); }
                  }}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                    active ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                           : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                  }`}
                  data-testid={`overlay-position-${pk}`}>{label}</button>
              );
            })}
            </div>
            </CollapsibleControlSection>
          </div>

          {/* STEP OVERLAY-FINAL-CONTROL-UX · Size Table Toggle Row (compact).
              Console 과 동일 contract (ops.toggleSizeTable · /announce body
              display:{size_table_visible}) · 활성 상품에 size_table 데이터가
              있을 때만 유의미하나 UI 는 항상 노출 (preset 유지). */}
          <div className="mb-3 p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
               data-testid="overlay-size-table-row"
               data-overlay-order="4"
               style={{ order: 4 }}
               data-disabled={!data?.active_product_id ? 'true' : 'false'}
               data-visible={data?.active_product_display?.size_table_visible ? 'true' : 'false'}>
            <CollapsibleControlSection
              panelKey="size-table"
              title="사이즈표"
              collapsed={overlayPanels.getPanel('size-table').collapsed}
              pinned={overlayPanels.getPanel('size-table').pinned}
              onToggle={() => overlayPanels.toggleCollapsed('size-table')}
              onPin={(v) => overlayPanels.setPinned('size-table', v)}
              testIdPrefix="overlay-size-table">
            <p className="text-[10px] text-gray-500 leading-snug mb-1.5"
               data-testid="overlay-size-table-helper">
              예: 사이즈표 Overlay {!data?.active_product_id && (
                <span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>
              )}
            </p>
            <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">사이즈표</span>
            {(() => {
              const visible = !!data?.active_product_display?.size_table_visible;
              const hasTable = !!(data?.active_product?.size_table
                && (data.active_product.size_table.rows || data.active_product.size_table.guide_text
                  || data.active_product.size_table.material || data.active_product.size_table.etc));
              const currentPreset = data?.active_product_display?.preset || 'default';
              return (
                <>
                  <button disabled={busy || !data?.active_product_id}
                    onClick={async () => {
                      // STEP OVERLAY-FINAL-CONTROL-UX · ops.toggleSizeTable REUSE.
                      setBusy(true);
                      try {
                        await ops.toggleSizeTable(data.active_product_id, currentPreset, !visible);
                        toast.success(visible ? '사이즈표 숨김' : '사이즈표 표시');
                        loadDetail();
                      } catch (e) { toast.error(e?.response?.data?.detail || '사이즈표 조작 실패'); }
                      finally { setBusy(false); }
                    }}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                      visible ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                              : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                    }`}
                    data-testid="overlay-size-table-toggle">
                    {visible ? '숨김' : '표시'}
                  </button>
                  {!hasTable && data?.active_product_id && (
                    <span className="text-[10px] text-gray-400 ml-1"
                          data-testid="overlay-size-table-empty-hint">
                      · 상품 사이즈표 데이터 없음
                    </span>
                  )}
                </>
              );
            })()}
            </div>
            </CollapsibleControlSection>
          </div>

          {/* STEP OVERLAY-2I-INVENTORY · Consumer Inventory Mode Row (compact).
              `applyInventoryMode(mode)` → POST /announce { product_id, display:{inventory_mode} }.
              판매자 내부에는 실제 재고 숫자/상태를 live-context 가 공급하므로 별도 UI 조정 X. */}
          <div className="mb-3 p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
               data-testid="overlay-inventory-row"
               data-overlay-order="5"
               style={{ order: 5 }}
               data-disabled={!data?.active_product_id ? 'true' : 'false'}
               data-inventory-mode={data?.active_product_display?.inventory_mode || 'hidden'}>
            <CollapsibleControlSection
              panelKey="inventory"
              title="재고 표시"
              collapsed={overlayPanels.getPanel('inventory').collapsed}
              pinned={overlayPanels.getPanel('inventory').pinned}
              onToggle={() => overlayPanels.toggleCollapsed('inventory')}
              onPin={(v) => overlayPanels.setPinned('inventory', v)}
              testIdPrefix="overlay-inventory">
            <p className="text-[10px] text-gray-500 leading-snug mb-1.5"
               data-testid="overlay-inventory-helper">
              예: 남은 수량 · 품절 임박 {!data?.active_product_id && (
                <span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>
              )}
            </p>
            <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">재고 표시</span>
            {[
              ['hidden', '숨김'],
              ['exact', '수량'],
              ['status', '상태'],
            ].map(([mk, label]) => {
              const cur = data?.active_product_display?.inventory_mode || 'hidden';
              const active = cur === mk;
              return (
                <button key={mk} disabled={busy || !data?.active_product_id}
                  onClick={async () => {
                    // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.setInventoryMode REUSE.
                    setBusy(true);
                    try {
                      await ops.setInventoryMode(data.active_product_id, mk);
                      toast.success(`재고 표시 · ${label}`);
                      loadDetail();
                    } catch (e) { toast.error(e?.response?.data?.detail || '설정 실패'); }
                    finally { setBusy(false); }
                  }}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                    active ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                           : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                  }`}
                  data-testid={`overlay-inventory-${mk}`}>{label}</button>
              );
            })}
            {/* 판매자 내부: 현재 활성 상품의 실제 재고 숫자 (소비자 영상과 분리). */}
            {(() => {
              const ap = data?.active_product;
              if (!ap) return null;
              const status = ap.stock_status;
              const avail = ap.available_stock;
              let txt = '';
              if (status === 'unlimited') txt = '무제한';
              else if (status === 'unknown') txt = '-';
              else if (Number.isFinite(Number(avail))) txt = `${Number(avail).toLocaleString('ko-KR')}개`;
              return (
                <span className="ml-auto text-[11px] font-bold text-gray-500"
                      data-testid="overlay-inventory-current">
                  현재 재고 · <span className={status === 'sold_out' ? 'text-[#FF3B30]' : status === 'low_stock' ? 'text-[#F27A18]' : ''}>{txt || '-'}</span>
                </span>
              );
            })()}
            </div>
            </CollapsibleControlSection>
          </div>

          {/* STEP OVERLAY-2J-QR · Active Product QR Overlay control.
              기존 /announce body 에 `qr:{action,duration_seconds}` additive. */}
          <div className="mb-3 p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
               data-testid="overlay-qr-row"
               data-overlay-order="2"
               style={{ order: 2 }}
               data-disabled={!data?.active_product_id ? 'true' : 'false'}>
            <CollapsibleControlSection
              panelKey="qr"
              title="QR"
              collapsed={overlayPanels.getPanel('qr').collapsed}
              pinned={overlayPanels.getPanel('qr').pinned}
              onToggle={() => overlayPanels.toggleCollapsed('qr')}
              onPin={(v) => overlayPanels.setPinned('qr', v)}
              testIdPrefix="overlay-qr">
            {/* STEP OVERLAY-CONTROL-UX-1 · QR 의미 설명 (지시 §C).
                QR 은 임의 URL 입력이 아니라 현재 소개 상품의 소비자 상품 페이지
                이동 수단임을 명시 — 판매자가 혼동하지 않도록. */}
            <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug mb-1"
               data-testid="overlay-qr-description">
              방송 화면에 상품 QR을 표시합니다. 시청자가 스캔하면 해당 상품 페이지로 이동합니다.
            </p>
            <p className="text-[10px] text-gray-500 leading-snug mb-1.5"
               data-testid="overlay-qr-helper">
              예: PC·TV 시청자 스캔 이동 {!data?.active_product_id && (
                <span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>
              )}
            </p>
            <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">QR</span>
            <button disabled={busy || !data?.active_product_id}
              onClick={async () => {
                // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.showQr REUSE.
                if (isTest) { testDo((c) => ({ ...c, active_product_qr: { visible: true, duration_seconds: 10 } }), 'TEST QR 표시'); return; }
                setBusy(true);
                try {
                  await ops.showQr(data.active_product_id, 10);
                  toast.success('QR 표시 (10초)');
                  loadDetail();
                } catch (e) { toast.error(e?.response?.data?.detail || 'QR 표시 실패'); }
                finally { setBusy(false); }
              }}
              className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="overlay-qr-show">QR 표시</button>
            <button disabled={busy || !data?.active_product_id}
              onClick={async () => {
                // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.hideQr REUSE.
                if (isTest) { testDo((c) => ({ ...c, active_product_qr: { visible: false } }), 'TEST QR 내림'); return; }
                setBusy(true);
                try {
                  await ops.hideQr(data.active_product_id);
                  toast.success('QR 내림');
                  loadDetail();
                } catch (e) { toast.error(e?.response?.data?.detail || 'QR 내리기 실패'); }
                finally { setBusy(false); }
              }}
              className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="overlay-qr-hide">QR 내리기</button>
            {data?.active_product_qr?.visible && (
              <span className="text-[10px] text-gray-500 ml-auto"
                    data-testid="overlay-qr-active">
                표시 중 · 만료 {data.active_product_qr.expires_at ? new Date(data.active_product_qr.expires_at.endsWith('Z') ? data.active_product_qr.expires_at : data.active_product_qr.expires_at + 'Z').toLocaleTimeString('ko-KR', {hour12: false}) : '-'}
              </span>
            )}
            </div>
            </CollapsibleControlSection>
          </div>

          {/* STEP OVERLAY-NOTICE-UX-CLEANUP-1 · 중복 NOTICE 입력 박스 (A) 제거.
              LIVE NOTICE backend/realtime API (`/seller/streaming/sessions/{sid}/notice`
              · useNoticeEditor hook) 는 그대로 유지되며,
              NoticeLibraryCard 가 '보관함 저장' + '공지 표시' + '내리기' 를 모두 담당한다.
              _noticeEditor hook 은 overlay-notice-library-row 의 onApply/onClear 에서
              계속 사용되고 있음 (clear 는 useNoticeEditor.clear() 를 통해 호출 가능). */}

          {/* STEP OVERLAY-2D-TIMER · event countdown control */}
          <div className="mb-3 p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
               data-testid="overlay-timer-section"
               data-overlay-order="6"
               style={{ order: 6 }}>
            <CollapsibleControlSection
              panelKey="timer"
              title="타이머"
              collapsed={overlayPanels.getPanel('timer').collapsed}
              pinned={overlayPanels.getPanel('timer').pinned}
              onToggle={() => overlayPanels.toggleCollapsed('timer')}
              onPin={(v) => overlayPanels.setPinned('timer', v)}
              testIdPrefix="overlay-timer">
            <OverlayTimerControl scheduleId={activeScheduleId}
                                   timer={data?.timer}
                                   disabled={busy}
                                   onLocalAction={isTest ? (a, e) => testDo((c) => presetOps.timer(c, a, e)) : undefined} />
            {showPresetsC && <div className="mt-2"><TimerPresetList ctx={presetCtxC} apply={presetApplyC} canStart={isTest || !!_realSid}
              onStartTimer={isTest ? undefined : (p) => realTimer(activeScheduleId, 'start', { duration_seconds: Math.round(p.minutes * 60), label: p.label }).then((ok) => { if (ok) loadDetail(); })} /></div>}
            </CollapsibleControlSection>
          </div>
          {isTest && <div className="mb-3" style={{ order: 7 }}><PastSlideStatus ctx={testCtx} /></div>}
          {/* STUDIO-NOTICE-EXTERNAL-UI-REORGANIZE-1 · 외부송출용 공지 — 타이머와 분리 (TEST 전용). */}
          {showPresetsC && (
            <div className="mb-3 self-start p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
                 data-testid="aux-external-notice-row" style={{ order: 7 }}>
              <HideBox label={`외부송출용 공지 · ${(presetCtxC?.external_notices || []).length}/5`} checked={hideExt} testid="aux-external-notice-hide"
                       onChange={(v) => { setHideExt(v); writeHidePref('external_notice', v); }} />
              {!hideExt && <ExternalNoticeList ctx={presetCtxC} apply={presetApplyC} showTitle={false} connected={!isTest} />}
            </div>
          )}
          {/* FINAL-VISUAL-MATCH-1 (지시 §9 §10) · 빠른등록 Chip — 부가기능 grid 마지막.
              CollapsibleControlSection 아님 (modal launcher) · 동일 tool-row visual language. */}
          <div className="mb-3 self-start p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02] inline-flex items-center justify-between gap-2"
               data-testid="aux-quick-add-chip"
               style={{ order: 7 }}>
            <button type="button"
                    onClick={() => setQuickOpen(true)}
                    className="flex-1 inline-flex items-center gap-1.5 text-left text-xs font-black text-gray-900 dark:text-white hover:text-[#FF3B30]"
                    data-testid="aux-quick-add-open">
              <Zap size={12} className="text-[#FF3B30]" /> 빠른등록
              {!(isTest && hideQuick) && <span className="text-[10px] text-gray-500 font-medium ml-1" data-testid="aux-quick-add-detail">(상품 즉시 등록)</span>}
            </button>
            {isTest && (
              <HideBox checked={hideQuick} testid="aux-quick-add-hide"
                       onChange={(v) => { setHideQuick(v); writeHidePref('quick_add', v); }} />
            )}
          </div>
          {/* STEP OVERLAY-CONTROL-UX-1 · 판매자 공용 NOTICE 보관함 (지시 §E/§F/§G/§H).
              · 특정 session 에 종속되지 않음 — LIVE 중이 아니어도 저장/수정/삭제 가능.
              · 선택 → "공지 표시" → 기존 LIVE NOTICE useNoticeEditor 로 반영 (REUSE).
              · 신규 upload endpoint 금지 — 내부에서 /uploads kind=broadcast REUSE.
              SECTION-CARD-FINAL-1 (지시 §6): 부가기능 grid 마지막 cell · 빠른등록(7) | NOTICE(8) 좌우 균형. */}
          {(() => {
            const _sid2 = data?.streaming_session_id;
            const _liveReady2 = !!_sid2 && data?.streaming_status === 'live';
            const _current = data?.live_notice || null;
            return (
              <div className="mb-3 p-2 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
                   data-testid="overlay-notice-library-row"
                   data-overlay-order="8"
                   style={{ order: 8 }}>
                <CollapsibleControlSection
                  panelKey="notice"
                  title={isTest ? 'NOTICE 보관함 · 자사몰 전용' : 'Notice 보관함'}
                  collapsed={overlayPanels.getPanel('notice').collapsed}
                  pinned={overlayPanels.getPanel('notice').pinned}
                  onToggle={() => overlayPanels.toggleCollapsed('notice')}
                  onPin={(v) => overlayPanels.setPinned('notice', v)}
                  testIdPrefix="overlay-notice">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <p className="text-[10px] text-gray-500 leading-snug flex-1 min-w-0">
                    자주 쓰는 공지를 저장해 다음 방송에서도 다시 사용하세요.
                  </p>
                  {_current && (_current.text || _current.image_url) && (
                    <button type="button"
                            onClick={async () => {
                              try {
                                if (isTest) { testDo((c) => ({ ...c, live_notice: null })); toast.info('LIVE 공지를 내렸어요.'); return; }
                                await _noticeEditor.clear();
                                toast.info('LIVE 공지를 내렸어요.');
                                loadDetail();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || '내리기 실패');
                              }
                            }}
                            disabled={isTest ? false : (!_liveReady2 || _noticeEditor.busy)}
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="overlay-notice-library-clear">
                      현재 공지 내리기
                    </button>
                  )}
                </div>
                {_current && (_current.text || _current.image_url) && !isTest && (
                  <p className="mb-1.5 text-[10px] text-emerald-700 dark:text-emerald-300"
                     data-testid="overlay-notice-library-current">
                    현재 LIVE 표시 중: {_current.text ? `"${_current.text.slice(0, 40)}"` : '(이미지 공지)'}
                  </p>
                )}
                {/* SELF-NOTICE-LIBRARY-SHARE-1 · TEST 현재 활성 공지 미리보기 (소비자 화면 동일 여부는 미검증 → 문구 상태만 표시). */}
                {isTest && (
                  <div className={`mb-1.5 rounded-lg border px-2 py-1.5 ${_current?.text ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`}
                       data-testid="overlay-notice-test-current">
                    <p className="text-[10px] font-black text-[#FF3B30]">{_current?.text ? 'TEST 공지 표시 중' : 'TEST · 표시 중인 공지 없음'}</p>
                    {_current?.text && (
                      <p className="mt-0.5 text-[11px] text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words"
                         data-testid="overlay-notice-test-current-text">{_current.text}</p>
                    )}
                  </div>
                )}
                <NoticeLibraryCard
                  library={noticeLibrary}
                  activeId={_current?.notice_id || null}
                  onApply={async (n) => {
                    // SELF-NOTICE-SYNC-UI-FIX-1 · TEST 는 공유 세션 live_notice 만 변경 (운영 공지 API 미호출).
                    if (isTest) {
                      const text = (n.text || '').trim();
                      if (!text) { toast.error('문구가 있는 공지만 표시할 수 있어요.'); return; }
                      testDo((c) => ({ ...c, live_notice: { text, notice_id: n.notice_id, updated_at: new Date().toISOString() } }));
                      toast.success('LIVE 공지를 반영했어요.');
                      return;
                    }
                    if (!_sid2) return;
                    try {
                      const body = { text: (n.text || '').trim(), notice_id: n.notice_id };
                      if (!body.text) { toast.error('문구가 있는 공지만 표시할 수 있어요.'); return; }
                      await api.put(`/seller/streaming/sessions/${_sid2}/notice`, body);
                      toast.success('LIVE 공지를 반영했어요.');
                      loadDetail();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || '공지 표시 실패');
                    }
                  }}
                  applyLabel="공지 표시"
                  applyDisabled={!isTest && !_liveReady2}
                  applyHint="LIVE 시작 후 방송 화면에 표시할 수 있습니다."
                  hideImage
                />
                </CollapsibleControlSection>
              </div>
            );
          })()}
          </div>
          {/* /live-control-overlay-sections (SECTION-CARD-FINAL-1: notice 를 grid 안으로 이동) */}
          </div>
          {/* /aux-features-header SECTION CARD close */}

          <div className="order-3 max-h-[480px] overflow-y-auto -mx-1 px-1" data-testid="live-products-ordered-wrapper">
          {(() => {
            // FINAL-VISUAL-MATCH-1 (지시 §7 §8) · local 검색 + max-h scroll.
            // 100+ 개 상품에서도 페이지가 길어지지 않도록 section 내부 scroll.
            const _q = productSearch.trim().toLowerCase();
            const filteredItems = _q ? items.filter((it) => (
              (it.name || '').toLowerCase().includes(_q)
              || String(it.product_id || '').toLowerCase().includes(_q)
              || String(it.sku || '').toLowerCase().includes(_q)
            )) : items;
          return items.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">등록된 라이브 상품이 없습니다. 위 버튼으로 상품을 추가하세요.</p>
          ) : filteredItems.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center" data-testid="live-products-search-empty">
              `{productSearch}` 검색 결과가 없습니다.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="live-products-list"
                data-items-total={items.length}
                data-items-filtered={filteredItems.length}>
              {filteredItems.map((it, i) => {
                const isActive = it.product_id === data?.active_product_id;
                const soldOut = (it.sale_status || '판매 중') === '품절';
                const isDragging = dragPid === it.product_id;
                const isDropTarget = dragOverPid === it.product_id && dragPid && dragPid !== it.product_id;
                return (
                  <li key={it.product_id}
                    draggable={!busy && !productSearch.trim()}
                    onDragStart={onDragStart(it.product_id)}
                    onDragOver={onDragOver(it.product_id)}
                    onDrop={onDrop(it.product_id)}
                    onDragEnd={onDragEnd}
                    className={`flex items-center gap-2 p-1.5 rounded-xl border transition-all
                      ${isActive ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}
                      ${soldOut ? 'opacity-60' : ''}
                      ${isDragging ? 'opacity-40' : ''}
                      ${isDropTarget ? 'border-2 border-dashed border-[#FF3B30] -translate-y-0.5 shadow-lg' : ''}
                      ${!busy ? 'cursor-grab active:cursor-grabbing' : ''}`}
                    data-testid={`live-product-${it.product_id}`}>
                    {/* drag handle · visual hint · PC + Mobile */}
                    <span className="shrink-0 text-gray-300 dark:text-white/20 select-none touch-none" title="드래그하여 순서 변경" data-testid={`drag-${it.product_id}`}>⋮⋮</span>
                    {/* order arrows — 기존 유지 */}
                    <div className="flex flex-col shrink-0">
                      <button disabled={busy || i === 0} onClick={() => move(it.product_id, -1)} className="p-0.5 text-xs disabled:opacity-30" data-testid={`up-${it.product_id}`}><ArrowUp size={11} /></button>
                      <button disabled={busy || i === items.length - 1} onClick={() => move(it.product_id, 1)} className="p-0.5 text-xs disabled:opacity-30" data-testid={`down-${it.product_id}`}><ArrowDown size={11} /></button>
                    </div>
                    {editSlot === it.product_id ? (
                      <SlotEditor current={it.slot_number} onSave={(v) => saveSlot(it.product_id, v)} onCancel={() => setEditSlot(null)} />
                    ) : (
                      <button onClick={() => setEditSlot(it.product_id)}
                        className="w-7 h-7 rounded-full bg-black text-white text-[11px] font-black inline-flex items-center justify-center shrink-0 hover:bg-[#FF3B30]"
                        title="번호 변경"
                        data-testid={`slot-${it.product_id}`}>
                        {it.slot_number}
                      </button>
                    )}
                    <SafeImg src={it.image} kind="product" className="w-9 h-9 rounded object-cover" alt="" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-bold truncate">{it.name}</p>
                      <p className="text-[10px] text-gray-500 inline-flex items-center gap-1">
                        {won(it.sale_price)} ·
                        {soldOut ? <span className="text-orange-600 font-bold">품절</span> : (it.sale_status || '판매 중')}
                      </p>
                    </div>
                    {isActive ? (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-black bg-[#FF3B30] text-white inline-flex items-center gap-1" data-testid={`active-badge-${it.product_id}`}>
                        <Megaphone size={11} /> 소개중
                      </span>
                    ) : it.is_introduced ? (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-black bg-emerald-600 text-white inline-flex items-center gap-1" data-testid={`introduced-badge-${it.product_id}`}>
                        <Check size={11} /> 소개완료
                      </span>
                    ) : (
                      <button onClick={() => announce(it.product_id)} disabled={busy || soldOut}
                        className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#FF3B30] text-white disabled:opacity-40 inline-flex items-center gap-1"
                        data-testid={`announce-${it.product_id}`}>
                        <Megaphone size={11} /> 소개하기
                      </button>
                    )}
                    {/* 다시 가리기 · introduced_only 모드에서 소개 완료/중 상품만 노출 */}
                    {visMode === 'introduced_only' && it.is_introduced && (
                      <button onClick={() => unintroduce(it.product_id)} disabled={busy}
                        className="px-2 py-1 rounded-full text-[10px] font-bold border border-black/20 dark:border-white/20 text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 inline-flex items-center gap-1"
                        title="다시 가리기 · 곧 진행 상태로 복귀"
                        data-testid={`unintroduce-${it.product_id}`}>
                        <XCircle size={11} /> 다시 가리기
                      </button>
                    )}
                    <button onClick={() => setEditSlot(it.product_id)} className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10" title="번호 변경" data-testid={`edit-slot-${it.product_id}`}><Pencil size={11} /></button>
                    <button onClick={() => removeItem(it.product_id)} className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10 text-red-600" title="제거" data-testid={`remove-${it.product_id}`}><Trash2 size={11} /></button>
                  </li>
                );
              })}
            </ul>
          );
          })()}
          </div>
                </div>
      )}
      </div>
      {/* /live-control-workarea */}

      {activeScheduleId && !isTest && (
        <p className="text-[11px] text-gray-400 text-center">
          소비자 라이브 화면으로 이동 → <Link className="text-[#FF3B30] font-bold hover:underline" to={`/live/${activeScheduleId}`} data-testid="live-view-link">소비자 화면 미리보기</Link>
        </p>
      )}

      </div>
      {/* /live-control-left */}

      {/* RIGHT · Monitor / 댓글·주문 / 채널 상태 (지시 §5 §7 §11).
          lg 이상에서 sticky + 내부 scroll — LEFT 상품 작업 중에도 Monitor/Response
          항상 가시.  mobile 에서는 자연스럽게 아래로 stack. */}
      <aside className="min-w-0 flex flex-col gap-3 lg:sticky lg:top-2 lg:self-start lg:max-h-[calc(100vh-1rem)] lg:overflow-y-auto lg:pr-1"
             data-testid="live-control-right">

      {/* STEP LIVE-CONSOLE-FINAL-UI-POLISH-1 · RIGHT 우선순위 재배치 (지시 §8).
          순서: 5 방송 모니터 → 6 댓글·주문 → 7 채널 연결 상태 → 상세 (YouTube/TikTok Connect).
          기존 anchor 는 모두 보존 — 댓글·주문을 TikTok/Instagram 대형 카드 아래로 밀어내지 않음. */}

      {/* STEP LIVE-CONSOLE-ROLE-SPLIT-UX-1 · 외부 LIVE 모니터 (RIGHT 영역 상단 ·
          새 창 열기 + 미니주문-only URL fallback · 신규 backend 0 · localStorage 전용). */}
      <BroadcastMonitorCard
        sellerId={user?.seller_id || ''}
        youtubeAutoUrl={''}
        scheduleId={activeScheduleId || ''}
        livemoaStatus={data?.streaming_status || null}
        onOpenLivemoa={isTest ? () => setTestViewerOpen(true) : null}
        livemoaTest={isTest}
      />
      {/* PC-CONSOLE-OVERLAY-PRESET-CONSUMER-VIEW-1 · TEST 소비자 보기 = Studio LivePreviewModal('viewer') REUSE. */}
      {isTest && testViewerOpen && (
        <LivePreviewModal kind="viewer" ctx={testCtx || {}} onClose={() => setTestViewerOpen(false)} backLabel="콘솔로 돌아가기" />
      )}

      {/* 6. 💬 댓글 주문 (Phase 6) — internal test input + candidate list.
          실제 TikTok/Instagram REAL 댓글 연동 아님 — 내부 테스트 입력/후보 리스트
          (지시 §9 · Mock 을 Production 처럼 과장 금지).  방송 모니터 바로 아래 배치. */}
      {activeScheduleId && !isTest && (
        <div id="phase7-comment-panel">
          <CommentOrderPanel scheduleId={activeScheduleId} />
        </div>
      )}

      {/* 7. STEP LIVE-CONSOLE-ROLE-SPLIT-UX-1 · 소셜 채널 연결 상태 통합 (RIGHT 하단).
          FINAL-UI-POLISH-1: LiveStreamCard (Camera/Mic/LiveKit publish UI) 제거.
          Studio (`SellerLiveConsole.js`) 가 Camera/Mic/LiveKit 를 전담 — Console 중복 노출만 제거. */}
      <div data-testid="live-control-channel-connections">
        <LiveChannelsCard compact />
        <details className="mt-2 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.015] dark:bg-white/[0.02]"
                 data-testid="channel-detail-settings">
          <summary className="px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 cursor-pointer select-none inline-flex items-center gap-1 w-full">
            상세 설정 <span className="text-[10px] text-gray-400 font-medium">(TikTok · Instagram OAuth · Mock/Test 안내)</span>
          </summary>
          <div className="p-2 border-t border-black/10 dark:border-white/10 flex flex-col gap-2">
            <TikTokAccountCard compact />
            <InstagramAccountCard compact />
          </div>
        </details>
      </div>

      {/* STEP LIVE-CONSOLE-FINAL-UI-POLISH-1 · YouTube/TikTok Connect 상세는
          기본 접힘 (지시 §12 · 개발/설정 정보가 운영 Console 핵심정보 아님).
          기존 OAuth/Mock 기능 삭제 금지 — 접기만 적용. */}
      {activeScheduleId && !isTest && (
        <details className="rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.015] dark:bg-white/[0.02]"
                 data-testid="connect-panels-details">
          <summary className="px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 cursor-pointer select-none inline-flex items-center gap-1 w-full">
            YouTube · TikTok 연동 상세 <span className="text-[10px] text-gray-400 font-medium">(API key / OAuth / mock)</span>
          </summary>
          <div className="p-2 border-t border-black/10 dark:border-white/10 flex flex-col gap-2">
            {/* 4. 🎥 YouTube Live 댓글 연동 (Phase 8) — hybrid API key / OAuth / mock */}
            <YouTubeConnectPanel scheduleId={activeScheduleId} />
            {/* 4-T. 🎬 TikTok LIVE 댓글 (Phase 14-T · Mock Adapter) */}
            <TikTokConnectPanel scheduleId={activeScheduleId} />
          </div>
        </details>
      )}

      {/* STEP MINI-CONSOLE-STUDIO-UX-1 · 사이드 (RIGHT) 는 **간편 관리 기능만** 유지.
          · 메인 승격된 ExternalMiniSection 와 중복 금지 — 사이드엔 자사 LIVE 패널 +
            외부 세션 compact 리스트 (생성 폼 X · 상태 전이 X · QR 토글 X).
          · SelfLivePanel 은 streaming_session_id 가 없으면 placeholder 만.
          · variant='compact' 로 외부 리스트는 URL 복사만 가능. */}
      <SelfLivePanel streamingSessionId={data?.streaming_session_id || null}
                      productsCount={Array.isArray(data?.live_products) ? data.live_products.length : 0} />
      <ExternalMiniSection defaultCollapsed variant="compact" />

      </aside>
      {/* /live-control-right */}

      </div>
      {/* /live-control-split */}

      <AddProductModal open={addOpen} onClose={() => setAddOpen(false)} currentIds={currentIds}
        scheduleId={activeScheduleId} onAdded={loadDetail} />
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)}
        currentScheduleId={activeScheduleId} onImported={loadDetail} />
      <QuickAddModal open={quickOpen} onClose={() => { setQuickOpen(false); setInboxCapture(null); }}
        initialImageFile={inboxCapture?.file || null} defaultAnnounce={!inboxCapture}
        isTestMode={isTest} uploadInTest onTestRegister={isTest ? testQuickRegister : null}
        scheduleId={activeScheduleId}
        onCreated={(created) => {
          if (inboxCapture) {
            markCaptureRegistered(activeScheduleId, inboxCapture.capture.capture_id, created?.product_id).catch(() => {});
            setInboxCapture(null);
          }
          loadDetail();
        }} />
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        broadcastId={activeScheduleId}
        slotNumber={activeItem?.slot_number || null}
        sellerName={sellerName}
        productName={activeItem?.name || ''}
        price={activeItem?.sale_price || 0}
      />
      <LiveReportModal
        scheduleId={activeScheduleId}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />
    </div>
  );
};

export default SellerLiveControl;
